// i18n — PT-PT default, EN selectable.
// Lightweight context-based provider (no i18next dep needed for MVP).
// Rule: all visible UI strings must come from translations.
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const ptPT = {
    common: {
        appName: "Ordly",
        loading: "A carregar...",
        save: "Guardar",
        cancel: "Cancelar",
        continue: "Continuar",
        back: "Voltar",
        skip: "Ignorar por agora",
        next: "Seguinte",
        signIn: "Iniciar sessão",
        signOut: "Terminar sessão",
        createWorkspace: "Criar workspace",
        languagePT: "Português",
        languageEN: "English",
        errorGeneric: "Não foi possível processar o pedido. Tente novamente.",
        supplierNifFallback: "Fornecedor NIF {nif}",
    },
    splash: {
        tagline1: "Smarter operations.",
        tagline2: "Simpler workflows.",
    },
    chooseWorkspace: {
        title: "Escolha o seu espaço",
        subtitle: "A Ordly serve empresas e contabilistas. Escolha por onde quer entrar — pode mudar a qualquer momento.",
        company: {
            title: "Espaço da Empresa",
            description: "Para empresas e colaboradores que organizam despesas e documentos.",
            cta: "Continuar para Espaço da Empresa",
        },
        accountant: {
            title: "Espaço do Contabilista",
            description: "Para gabinetes de contabilidade que gerem clientes, equipas e exceções.",
            cta: "Continuar para Espaço do Contabilista",
        },
        switchToAccountantPrompt: "É contabilista?",
        switchToAccountantCta: "Ir para o Espaço do Contabilista",
    },
    accountantInvite: {
        loading: "A carregar convite…",
        readyTitle: "Convite de Contabilista",
        readyBody: "Reveja a identidade do escritório e aceite ou recuse este convite. A Ordly não partilhou dados da sua empresa antes da sua aceitação.",
        firmLabel: "Escritório",
        companyLabel: "Empresa",
        expiresLabel: "Expira em",
        onlyOwnerHint: "Apenas um proprietário verificado da empresa pode aceitar ou recusar este convite.",
        acceptButton: "Aceitar e ligar",
        acceptingButton: "A aceitar…",
        rejectButton: "Recusar",
        rejectingButton: "A recusar…",
        acceptedTitle: "Convite aceite",
        acceptedBody: "A ligação ao escritório de contabilidade está agora ativa.",
        rejectedTitle: "Convite recusado",
        rejectedBody: "Comunicámos a recusa ao escritório de contabilidade.",
        continue: "Continuar para o meu espaço",
        backToWorkspace: "Voltar ao meu espaço",
        errorTitle: "Não foi possível abrir o convite",
        error: {
            generic: "Ocorreu um problema. Tente novamente ou contacte o escritório.",
            invitation_not_found: "Este convite não foi encontrado ou já não está disponível.",
            invitation_already_resolved: "Este convite já foi processado.",
            invitation_expired: "Este convite expirou.",
            only_owner_may_accept: "Apenas um proprietário verificado pode aceitar este convite.",
            only_owner_may_reject: "Apenas um proprietário verificado pode recusar este convite.",
            email_not_verified: "Confirme primeiro o seu email para aceitar este convite.",
            company_already_connected: "A sua empresa já está ligada a outro escritório de contabilidade.",
        },
    },
    sponsorshipInvite: {
        loading: "A carregar convite…",
        // Stage 5E.2 Phase 6 — copy bloqueada (contrato §21.1).
        readyTitle: "Ative o seu espaço",
        readyBody: "O seu escritório de contabilidade preparou este espaço para si. Reveja e ative para começar.",
        // Chaves Stage 5D retidas uma versão (rollback per §21.1).
        readyTitleV1: "Ativar Ordly",
        readyBodyV1: "O seu escritório de contabilidade preparou um espaço Ordly para si. Reveja e ative para começar.",
        firmLabel: "Escritório",
        clientLabel: "Empresa",
        recipientLabel: "Email",
        expiresLabel: "Expira em",
        brand: {
            brandedHeading: "{firm} preparou o seu espaço de cliente",
            brandedBody: "{firm} preparou {client} para si.",
            brandedHeadingV1: "{firm} convidou-o(a) para a Ordly",
            logoAlt: "Logótipo de {firm}",
            poweredByOrdly: "Powered by Ordly · getordly.app",
        },
        signInPromptTitle: "Já tem conta Ordly?",
        signInPromptBody: "Inicie sessão para continuar.",
        signInButton: "Iniciar sessão e continuar",
        createAccountTitle: "Criar a sua conta",
        createAccountBody: "Defina a sua palavra-passe para ativar este espaço.",
        fullNameLabel: "Nome completo",
        passwordLabel: "Palavra-passe",
        acceptButton: "Ativar o seu espaço",
        acceptButtonV1: "Ativar e entrar",
        acceptingButton: "A ativar…",
        rejectButton: "Recusar",
        rejectingButton: "A recusar…",
        acceptedActiveTitle: "O seu espaço está pronto",
        acceptedActiveTitleV1: "Espaço ativado",
        acceptedActiveBody: "O seu espaço de cliente está pronto. Será encaminhado.",
        acceptedPendingTitle: "Aguardar confirmação",
        acceptedPendingBody: "Já existe uma empresa com este NIF na Ordly. Enviámos um pedido ao proprietário para confirmar a ligação.",
        rejectedTitle: "Convite recusado",
        rejectedBody: "Comunicámos a recusa ao escritório de contabilidade.",
        continue: "Continuar",
        backToLogin: "Ir para o início de sessão",
        errorTitle: "Não foi possível abrir o convite",
        error: {
            generic: "Ocorreu um problema. Tente novamente ou contacte o escritório.",
            sponsorship_invitation_not_found: "Este convite não foi encontrado ou já não está disponível.",
            sponsorship_invitation_already_resolved: "Este convite já foi processado.",
            sponsorship_invitation_expired: "Este convite expirou.",
            recipient_email_mismatch: "Este convite foi enviado para outro endereço de email.",
            authentication_required: "Inicie sessão para continuar.",
            ownership_verification_required: "Já existe uma empresa registada com este NIF. Pediremos confirmação ao proprietário.",
            sponsored_slot_exhausted: "O escritório atingiu o limite de espaços patrocinados. Contacte o seu contabilista.",
            company_already_connected: "Esta empresa já está ligada a outro escritório.",
            relationship_already_exists_same_workspace: "Já existe um pedido em curso para esta empresa.",
            nif_requires_manual_resolution: "Existem várias empresas com este NIF. Contacte o seu contabilista.",
            missing_credentials: "Indique o nome e palavra-passe para ativar.",
        },
    },
    welcome: {
        headline: "Fotografe despesas.",
        headline2: "Entregue o mês organizado.",
        intro: "Guarde faturas e talões ao longo do mês. A Ordly organiza tudo num pacote pronto para enviar ao contabilista.",
        primary: "Criar workspace",
        secondary: "Iniciar sessão",
        support: "Comece agora. Convide o seu contabilista quando quiser.",
    },
    landing: {
        heroPill: "Para pequenas empresas em Portugal",
        heroTitle: "Chega de faturas num saco.",
        heroSub:
            "Guarde despesas ao longo do mês. A Ordly organiza tudo e prepara um pacote limpo para o seu contabilista.",
        ctaPrimary: "Começar a organizar despesas",
        ctaSecondary: "Já tenho conta",
        support: "Comece sozinho. Ligue o seu contabilista quando estiver pronto.",
        featuresTitle: "Tudo o que precisa para o mês ficar pronto",
        f1Title: "Tire uma foto em segundos",
        f1Desc: "Sem formulários. A Ordly processa o documento depois.",
        f2Title: "Talões em lote, sem dor",
        f2Desc: "Digitalize muitos talões de papel de uma vez.",
        f3Title: "Detecta fotos tremidas e duplicados",
        f3Desc: "Antes que se torne um problema para o contabilista.",
        f4Title: "Veja se o mês está pronto",
        f4Desc: "Uma percentagem clara, sem stress.",
        f5Title: "Pacote limpo para o contabilista",
        f5Desc: "ZIP, CSV e resumo — partilhado por link seguro.",
        f6Title: "O contabilista valida a contabilidade",
        f6Desc: "A Ordly prepara contexto. Você mantém o controlo.",
        safetyLine: "A Ordly prepara documentos e contexto. O contabilista valida a contabilidade.",
        footerNote: "Ordly · Documentos e despesas para pequenas empresas em Portugal.",
    },
    auth: {
        registerTitleL1: "Criar workspace",
        registerTitleL2: "da empresa",
        registerTitle: "Criar workspace da empresa",
        createWorkspaceTitle: "Criar workspace\nda empresa",
        createWorkspaceSubtitle: "Organize faturas e recibos. Poupe tempo, mantenha-se em ordem e deixe o seu negócio pronto.",
        createWorkspace: "Criar workspace",
        registerSubtitle: "Organize faturas e recibos. Poupe tempo, mantenha-se em ordem e deixe o seu negócio pronto.",
        promise: "Tire uma foto. Mantenha o mês pronto.",
        promisePill: "Tire uma foto. Mantenha o mês pronto.",
        welcomeBack: "Bem-vindo de volta",
        loginTitle: "Bem-vindo de volta",
        loginSubtitle: "Inicie sessão para se manter a par dos seus documentos, prazos e fim de mês.",
        signIn: "Iniciar sessão",
        sendMagicLink: "Enviar link de acesso",
        alreadyHaveAccount: "Já tem conta?",
        dontHaveAccount: "Ainda não tem conta?",
        companyName: "Nome da empresa",
        companyNamePh: "ex. Ordly Solutions, Lda.",
        nif: "NIF",
        nifPh: "ex. 500 123 456",
        nifRequired: "O NIF da empresa é obrigatório.",
        nifInvalid: "O NIF introduzido não parece válido. Verifique os 9 dígitos.",
        nifPersonalNotice: "Este NIF parece ser de pessoa singular. Se trabalha como ENI/recibos verdes, pode continuar.",
        businessType: "Tipo de negócio",
        businessTypePh: "Selecione o tipo de negócio",
        email: "Email",
        emailPh: "ex. ola@ordly.com",
        password: "Palavra-passe",
        passwordPh: "Crie uma palavra-passe forte",
        passwordPhLogin: "Introduza a sua palavra-passe",
        rememberDevice: "Lembrar este dispositivo",
        forgotPassword: "Esqueceu-se da palavra-passe?",
        magicLink: "Enviar link de acesso",
        magicLinkSentTitle: "Link enviado",
        magicLinkSentBody: "Verifique o seu email. O link expira em 15 minutos.",
        magicLinkDemo: "Email simulado em ambiente demo. Use o token abaixo para entrar:",
        magicLinkConsume: "Entrar com link",
        noAccount: "Ainda não tem conta?",
        hasAccount: "Já tem conta?",
        createOne: "Criar conta",
        signInLink: "Iniciar sessão",
        // Temporary private-pilot state: the public Register CTA is rendered
        // disabled across Welcome, Login switch link and Landing. Direct
        // /register access is intentionally unaffected.
        pilotBadge: "Piloto privado",
        pilotHelp: "A Ordly está atualmente disponível apenas por convite.",
        legal: "A criar conta, aceita os Termos e a Política de Privacidade. Os seus documentos são privados e cifrados.",
        termsAcceptPrefix: "Aceito os ",
        termsLink: "Termos",
        termsAcceptAnd: " e a ",
        privacyLink: "Política de Privacidade",
        termsAcceptSuffix: ".",
        termsRequired: "Para criar conta, precisa de aceitar os Termos e a Política de Privacidade.",
        serviceUnavailable: "Estamos com dificuldades temporárias. Tente de novo em alguns segundos.",
        trustNote: "Os seus dados estão seguros. Usamos cifragem padrão da indústria para proteger as suas informações.",
        registerCTA: "Criar workspace",
        loginCTA: "Iniciar sessão",
        or: "ou",
        // Phase 11 — email verification
        verifyEmailTitle: "Confirme o seu email",
        verifyEmailHelper: "Enviámos um código de 6 dígitos para {email}.",
        verifyEmailInstruction: "Introduza o código para ativar a sua conta.",
        verifyEmailCodeLabel: "Código de confirmação",
        verifyEmailCodePh: "6 dígitos",
        verifyEmailConfirmCta: "Confirmar email",
        verifyEmailResendCta: "Enviar novo código",
        verifyEmailResendSent: "Novo código enviado. Verifique o seu email.",
        verifyEmailLoginRequired: "Confirme o seu email para continuar.",
        verifyEmailErrorInvalid: "Código inválido. Verifique e tente novamente.",
        verifyEmailErrorExpired: "Este código expirou. Peça um novo código.",
        verifyEmailErrorTooMany: "Demasiadas tentativas. Peça um novo código.",
        verifyEmailErrorResendSoon: "Aguarde alguns segundos antes de pedir um novo código.",
        verifyEmailErrorGeneric: "Não foi possível confirmar. Tente novamente.",
        verifyEmailSendFailed: "Não foi possível enviar o código. Tente novamente.",
        verifyEmailSendFailedRetry: "Toque em \"Enviar novo código\" para tentar de novo.",
        // Phase 12 — password recovery (PT)
        forgotPasswordTitle: "Recuperar palavra-passe",
        forgotPasswordHelper: "Indique o seu email para receber um código de 6 dígitos.",
        forgotPasswordCta: "Enviar código",
        forgotPasswordSent: "Se este email existir, foi enviado um código para recuperar a palavra-passe.",
        forgotPasswordBackToLogin: "Voltar ao início de sessão",
        resetPasswordTitle: "Defina uma nova palavra-passe",
        resetPasswordHelper: "Introduza o código que enviámos para {email} e a sua nova palavra-passe.",
        resetPasswordCodeLabel: "Código de 6 dígitos",
        resetPasswordCodePh: "6 dígitos",
        resetPasswordNewLabel: "Nova palavra-passe",
        resetPasswordNewPh: "Pelo menos 8 caracteres",
        resetPasswordCta: "Atualizar palavra-passe",
        resetPasswordSuccess: "A sua palavra-passe foi atualizada. Pode iniciar sessão.",
        resetPasswordSignIn: "Iniciar sessão",
        resetPasswordResendCta: "Enviar novo código",
        resetPasswordErrorInvalid: "Código inválido. Verifique e tente novamente.",
        resetPasswordErrorExpired: "Este código expirou. Peça um novo código.",
        resetPasswordErrorTooMany: "Demasiadas tentativas. Peça um novo código.",
        resetPasswordErrorResendSoon: "Aguarde alguns segundos antes de pedir um novo código.",
        resetPasswordErrorWeak: "A palavra-passe deve ter pelo menos 8 caracteres.",
        resetPasswordErrorGeneric: "Não foi possível atualizar. Tente novamente.",
        biz: {
            creative: "Estúdio criativo",
            cafe: "Café / Restaurante",
            shop: "Comércio / Loja",
            services: "Serviços profissionais",
            tech: "Tecnologia / Digital",
            other: "Outro",
        },
    },
    legal: {
        backToRegister: "Voltar ao registo",
        termsTitle: "Termos de Utilização",
        termsLastUpdated: "Última atualização: junho de 2026",
        termsIntro: "Bem-vindo à Ordly. Ao utilizar a aplicação, aceita estes termos. Esta é uma versão MVP — os termos definitivos serão disponibilizados antes da nossa saída de fase beta.",
        termsAccountTitle: "1. A sua conta",
        termsAccountBody: "É responsável por manter as suas credenciais seguras e por toda a atividade efectuada na sua conta.",
        termsUseTitle: "2. Utilização do serviço",
        termsUseBody: "A Ordly destina-se a organizar documentos fiscais (faturas, talões, etc.). Não tomamos decisões contabilísticas nem fiscais no seu lugar.",
        termsDataTitle: "3. Os seus dados",
        termsDataBody: "Os seus documentos pertencem-lhe. Armazenamo-los cifrados e nunca os vendemos. Para mais detalhes, consulte a Política de Privacidade.",
        termsLiabilityTitle: "4. Limitação de responsabilidade",
        termsLiabilityBody: "A Ordly é fornecida \"tal como está\". Esforçamo-nos por manter o serviço disponível e correcto, mas não somos responsáveis por decisões contabilísticas tomadas com base nos dados organizados.",
        termsChangesTitle: "5. Alterações",
        termsChangesBody: "Podemos actualizar estes termos. Em caso de alterações materiais, será notificado por email.",
        termsContact: "Dúvidas? Contacte support@ordly.com",
        privacyTitle: "Política de Privacidade",
        privacyLastUpdated: "Última atualização: junho de 2026",
        privacyIntro: "Esta política descreve, em termos simples, os dados que recolhemos, porquê, e como pode controlá-los.",
        privacyWhatTitle: "1. O que recolhemos",
        privacyWhatBody: "Nome da empresa, NIF, email, idioma preferido, e os documentos que carrega (faturas, talões, fotos).",
        privacyWhyTitle: "2. Porque o usamos",
        privacyWhyBody: "Para organizar os seus documentos, lembrá-lo de prazos e preparar pacotes para o seu contabilista. Nunca usamos os seus documentos para treinar modelos públicos de IA.",
        privacyShareTitle: "3. Partilha",
        privacyShareBody: "Não vendemos os seus dados. Apenas partilhamos com fornecedores estritamente necessários para a operação do serviço (alojamento, base de dados).",
        privacyRightsTitle: "4. Os seus direitos",
        privacyRightsBody: "Pode exportar ou apagar os seus dados em qualquer altura a partir de Definições, ou contactando-nos por email.",
        privacyContact: "Dúvidas? Contacte privacy@ordly.com",
    },
    onboarding: {
        stepN: "Passo {n} de {total}",
        reassure: "Demora menos de 3 minutos.",
        s1Title: "Vamos configurar a empresa",
        s1Sub: "Estes dados ajudam-nos a preparar o seu workspace.",
        s1Detail: "Nome da empresa · NIF · Tipo de negócio · Idioma — já preenchidos a partir do registo.",
        languageLabel: "Idioma preferido",
        s2Title: "Convide a equipa",
        s2Sub: "Pode adicionar utilizadores depois.",
        s3Title: "Adicione o contabilista",
        s3Sub: "Pode ligar mais tarde, quando estiver pronto.",
        s4TitleL1: "Adicionar a Ordly ao",
        s4TitleL2: "Ecrã Principal",
        s4Title: "Adicionar a Ordly ao Ecrã Principal",
        s4Sub: "Aceda à Ordly num toque para capturar talões, carregar documentos e manter-se a par do seu negócio.",
        pwaStep1Title: "Toque no ícone Partilhar",
        pwaStep1Desc: "Toque no botão Partilhar no seu browser.",
        pwaStep2Title: "Escolha Adicionar ao Ecrã Principal",
        pwaStep2Desc: "Desça e selecione “Adicionar ao Ecrã Principal”.",
        pwaStep3Title: "Abra a Ordly como uma app",
        pwaStep3Desc: "Encontre a Ordly no Ecrã Principal e abra-a quando quiser.",
        pwaInfoTitle: "Acesso rápido. Pronto.",
        pwaInfoDesc: "Abra a Ordly no Ecrã Principal para capturar talões e tratar do mês.",
        pwaStep1: "Toque em Partilhar",
        pwaStep2: "Escolha Adicionar ao Ecrã Principal",
        pwaStep3: "Abra a Ordly como uma app",
        finishedTitle: "Carregue o seu primeiro documento",
        finishedSub: "A Ordly começa já a preparar o mês.",
        takeFirstPhoto: "Tirar primeira foto",
        uploadDocument: "Carregar documento",
    },
    capture: {
        title: "Capturar despesa",
        chooseSource: "Escolha como adicionar",
        takePhoto: "Tirar foto",
        takePhotoHint: "Usa a câmara — fatura, talão ou QR.",
        pickFromGallery: "Escolher da galeria",
        pickFromGalleryHint: "Imagens, HEIC ou PDFs.",
        dropOrClick: "Arraste um ficheiro ou toque para escolher",
        accepted: "JPG · PNG · HEIC · WebP · PDF (até 20 MB)",
        thanksToast: "Obrigado — documento adicionado à inbox.",
        batchTitle: "Vários documentos",
        batchHint: "Até {max} numa só sessão",
        sourceEmailTitle: "Receber por email",
        sourceEmailHint: "Encaminhe faturas para a sua empresa",
        batchReviewTitle: "Rever antes de carregar",
        batchDoneTitle: "Tudo carregado",
        batchCount: "{n} de {max} documentos",
        batchAddMore: "Adicionar",
        batchAddCamera: "Tirar mais uma foto",
        batchUploadCta: "Carregar {n} documentos",
        batchUploadingCta: "A carregar… {done}/{total}",
        batchSeeInbox: "Ver inbox",
        batchRetryFailed: "Reprocessar falhas ({n})",
        batchCancel: "Cancelar",
        batchRemove: "Remover",
        batchErrorHint: "Falhou — tente novamente.",
        batchDoneSummary: "{ok} prontos · {err} com erro",
        batchStatus: {
            queued: "Em espera",
            optimizing: "A otimizar",
            uploading: "A carregar",
            processing: "A processar",
            done: "Pronto",
            error: "Erro",
        },
        optimizing: "A otimizar imagem…",
        uploading: "A enviar… {pct}%",
        processing: "A ler dados da fatura…",
        step1Preparing: "A preparar imagem…",
        step2QR: "A ler QR fiscal…",
        step3Invoice: "A ler dados da fatura…",
        step4Saving: "A guardar documento…",
        processingHelper: "Isto pode demorar alguns segundos.",
        done: "Documento pronto",
        savedTitle: "Guardado!",
        savedHelper: "Continue. Vamos terminar a leitura por si.",
        readingInBackground: "A finalizar análise…",
        // Phase A regression fix — restore the staged progress beat
        // (non-blocking; runs alongside the Saved/Add-another CTAs).
        // Five stages with % so progress feels measurable. 100% only
        // when the backend has actually returned final data.
        bgStep1: "Documento guardado",
        bgStep2: "A ler QR fiscal…",
        bgStep3: "A ler dados da fatura…",
        bgStep4: "A finalizar análise…",
        bgStep5: "Dados prontos para rever",
        pollTimeoutSavedKeepReading: "Documento guardado · a leitura pode continuar na caixa de entrada.",
        failed: "Falha ao processar",
        retry: "Tentar novamente",
        addAnother: "Adicionar outro",
        goToInbox: "Ver na caixa de entrada",
        sizeBefore: "Antes: {size}",
        sizeAfter: "Depois: {size}",
        ocrDemoBadge: "OCR DEMO",
        ocrRealBadge: "Dados extraídos por OCR",
        qrBadge: "QR fiscal lido",
        qrPromptTitle: "Adicionar foto do QR?",
        qrPromptHelper: "Uma foto aproximada do QR ajuda a ler os dados oficiais da fatura.",
        qrPromptAddCta: "Adicionar QR",
        qrPromptSkipCta: "Continuar sem QR",
        qrPromptCloseupHint: "Fotografe apenas o QR fiscal. Mantenha o papel esticado e aproxime o QR.",
        possibleDuplicate: "Possível duplicado",
        cameraSource: "Câmara",
        uploadSource: "Carregar",
        cancel: "Cancelar",
        close: "Fechar",
        // Phase 16 Stage 4 — extracted-fields card labels for the success
        // state. Previously hardcoded inside ``Capture.jsx``; moved here
        // so EN mode renders properly.
        metaTotal: "Total",
        metaNumber: "Nº",
        metaDate: "Data",
        metaCategory: "Categoria",
        // ------- Phase Pay (Jul 2026) — payment method prompt -------
        // Shown ONLY after the document has been safely saved
        // (phase === DONE) and the row is not pre-marked personal.
        // The save itself never waits for this — see Capture.jsx.
        paymentTitle: "Como foi pago?",
        paymentHelper: "Opcional — pode adicionar ou alterar esta informação mais tarde no detalhe do documento.",
        paymentSkip: "Adicionar depois",
        paymentSaving: "A guardar…",
        paymentSaved: "Guardado",
        // Phase Pay revision (Jul 2026) — on failure, show the
        // user-facing copy below instead of the generic capture error.
        paymentFailed: "Não foi possível guardar. Tente novamente.",
        // Display-only label for the NULL state, used in Document
        // Detail and Inbox tooltips. NEVER stored.
        paymentNotSpecified: "Não indicado",
        // Action label that pairs with the Saved! row — explicit
        // primary action for "guardar e adicionar outro".
        // Phase Pay revision (Jul 2026) — the spec replaces
        // "Guardar e adicionar outro" with plain "Adicionar outro"
        // because the document AND the payment are already
        // persisted before this action is offered. Keep the key for
        // any callsite that still imports it; both keys resolve to
        // the same label.
        saveAndAddAnother: "Adicionar outro",
        viewDocument: "Ver documento",
        // Enum labels. The KEY (left side of each entry below) is the
        // stable enum value the backend stores; the VALUE is the
        // PT-PT label rendered in the UI. NEVER send the label to the
        // backend — always send the key.
        paymentMethods: {
            company_card: "Cartão da empresa",
            company_bank_account: "Conta bancária da empresa",
            cash: "Dinheiro",
            personal_method: "Meio pessoal",
            unpaid: "Ainda não foi pago",
        },
        // ------- Phase 17 (Jul 2026) — Document context prompt -------
        // Rendered on the DONE branch of the single-capture success
        // screen, visually secondary to the completed-capture actions.
        // Saves ONLY `document_context_note` via PATCH; the document
        // itself is already persisted and processing is never
        // restarted. See Capture.jsx <ContextNotePrompt/>.
        contextTitle: "Adicionar contexto",
        contextHelper: "Opcional — será partilhado com o seu contabilista.",
        contextEmployeeHelper: "Opcional — será partilhado com a empresa e o contabilista.",
        contextPlaceholder: "Ex.: refeição com cliente, deslocação, material para o escritório…",
        contextSave: "Guardar contexto",
        contextSaved: "Guardado",
        contextFailed: "Não foi possível guardar. Tente novamente.",
    },
    inbox: {
        title: "Caixa de entrada",
        empty: "Ainda sem documentos",
        emptyHint: "Tire uma foto da primeira fatura para começar.",
        startCapture: "Tirar primeira foto",
        addManually: "Carregar documento",
        sectionRecent: "Recentes",
        statusReceived: "Recebido",
        statusReading: "A ler",
        statusReady: "Pronto",
        statusNeedsContext: "Precisa de contexto",
        statusNeedsNewPhoto: "Foto a refazer",
        statusPossibleDuplicate: "Possível duplicado",
        statusFailed: "Erro",
        statusPersonal: "Pessoal",
        statusMissingReceipt: "Sem talão",
        filterAll: "Todos",
        filterPending: "A tratar",
        filterReady: "Prontos",
        filterPersonal: "Pessoal",
        filterMissing: "Sem talão",
        filterSent: "Enviados",
        filterEmail: "Email",
        sourceEmailBadge: "Recebido por email",
        // Phase Pay (Jul 2026) — selective Inbox chips. Shown ONLY for
        // ``personal_method`` and ``unpaid``; suppressed for
        // company_card / company_bank_account / cash / NULL because
        // those are the implicit "company normal" path. Quiet chip,
        // secondary to status / supplier / amount / date.
        chipPersonalMethod: "Meio pessoal",
        chipUnpaid: "Por pagar",
        monthFilterLabel: "Mês",
        monthFilterAll: "Todos os meses",
        headingCountOne: "{n} documento",
        headingCountMany: "{n} documentos",
        sentOn: "Enviado em {date}",
        emptySentTitle: "Ainda não enviou nenhum documento.",
        emptySentBody: "Os documentos aparecerão aqui depois de serem incluídos num pacote para o contabilista.",
        monthContext: "A mostrar documentos de {month}",
        viewAllMonths: "Ver todos os meses",
        searchPlaceholder: "Procurar fornecedor ou número…",
        pendingTitle: "A precisar de si",
        pendingNone: "Tudo em ordem por agora.",
        noResults: "Sem resultados.",
        emptyMonthGeneric: "Sem documentos em {month}",
        emptyMonthPending: "Sem documentos por rever em {month}",
        emptyMonthReady: "Sem documentos prontos em {month}",
        emptyMonthNeedsPhoto: "Sem documentos a precisar de nova foto em {month}",
        emptyMonthPersonal: "Sem documentos pessoais em {month}",
        emptyMonthMissing: "Sem despesas sem talão em {month}",
        emptyMonthHelper: "Pode mudar o filtro ou ver todos os meses.",
        backToInbox: "Voltar à caixa de entrada",
    },
    detail: {
        title: "Detalhe da despesa",
        backBottom: "Voltar",
        extractedFrom: "Dados extraídos",
        assignedMonth: "Documento atribuído a {month}",
        dateSourceQrLabel: "Lida pelo QR fiscal",
        dateEditCorrectLabel: "Corrigir data extraída",
        dateEditAddLabel: "Adicionar data da fatura",
        dateEditHint: "Esta data deve corresponder à data que aparece na fatura.",
        previewUnavailable: "Não foi possível pré-visualizar o documento.",
        previewOpenOriginal: "Abrir documento original",
        previewPdfHint: "Este documento é um PDF. Abra-o para o visualizar.",
        // Internal viewer route (`/documents/:id/viewer`) — Phase 16
        // Feb 2026 mobile-first replacement for the previous
        // `target="_blank"` link that stranded iOS users outside the
        // app. Title is shown in the viewer's Ordly header next to
        // the Voltar back button.
        viewerTitle: "Documento original",
        viewerLoading: "A carregar documento…",
        viewerFallbackMsg: "O dispositivo não consegue mostrar o ficheiro aqui. Toque para abrir noutro separador.",
        viewerFallbackCta: "Abrir noutro separador",
        viewerOpenCta: "Abrir documento original",
        viewerOpenInSafari: "Abrir original no Safari",
        // Reprocess action — Feb 2026. Surfaces on documents whose
        // extraction failed or returned nothing usable, e.g. when
        // the EDP multipage QR failed before libzbar0 was deployed.
        // The action re-runs the canonical processor against the
        // SAME stored original (no new upload, no new Document row).
        reprocessAction: "Reprocessar documento",
        reprocessHint: "Vamos reler o mesmo ficheiro com o motor de extração atual.",
        reprocessInProgress: "A reprocessar…",
        reprocessSuccess: "Reprocessamento concluído.",
        reprocessAlreadyInProgress: "Já está em reprocessamento.",
        reprocessError: "Não foi possível reprocessar. Tente novamente.",
        // The canonical processor failed mid-run — distinct from a
        // network error during the retry HTTP call.
        reprocessFailed: "O reprocessamento falhou. Tente novamente ou contacte o suporte.",
        // Bounded polling timed out — the backend appears stuck.
        // Surface a manual refresh CTA rather than spin forever.
        reprocessTimeout: "O processamento está a demorar mais do que o esperado.",
        reprocessRefresh: "Atualizar estado",
        // Multipage PDF page-count label shown below the first-page
        // preview on Document Detail. Only rendered when n > 1; for
        // single-page PDFs the label is suppressed. ``{n}`` is the
        // total page count returned by the server.
        previewPdfPageLabel: "1 de {n} páginas",
        sourceQr: "QR fiscal lido",
        sourceOcr: "Confirme estes valores",
        sourceOcrReal: "Dados extraídos por OCR",
        sourceEmail: "Recebido por email",
        emailFrom: "De",
        emailSubject: "Assunto",
        emailReceived: "Recebido em",
        emailFilename: "Ficheiro",
        qrSourceHint: "Dados lidos diretamente do QR da fatura.",
        ocrConfirmBadge: "Confirme os valores",
        ocrDemoBadge: "OCR DEMO",
        nifMismatchTitle: "O NIF da fatura não corresponde ao NIF da empresa registada.",
        nifMismatchHint: "Confirme se este documento pertence à empresa antes de o incluir no pacote.",
        nifMismatchToast: "Documento guardado, mas precisa de confirmação: NIF diferente.",
        qrBadge: "QR detetado",
        supplier: "Fornecedor",
        supplierNif: "NIF do fornecedor",
        amount: "Total",
        date: "Data",
        documentType: "Tipo",
        invoiceNumber: "Número",
        category: "Categoria",
        notes: "Notas para o contabilista",
        notesPlaceholder: "Algum contexto útil para o seu contabilista…",
        // Phase 17 (Jul 2026) — Editable document context (canonical
        // ``document_context_note``). Distinct from ``notes_for_accountant``.
        contextSectionTitle: "Contexto do documento",
        contextAdd: "Adicionar contexto",
        contextSave: "Guardar contexto",
        contextPlaceholder: "Ex.: refeição com cliente, deslocação, material para o escritório…",
        contextSaved: "Guardado",
        contextFailed: "Não foi possível guardar. Tente novamente.",
        // Phase Pay (Jul 2026) — Document Detail payment-method strip
        // Phase Pay (Jul 2026) — Document Detail payment-method strip.
        // Spec calls this section "Forma de pagamento" (the source-
        // of-truth view) — distinct from the success-screen "Como foi
        // pago?" question. NULL renders as ``paymentNotSpecified``.
        paymentSectionTitle: "Forma de pagamento",
        paymentSectionEmpty: "Não indicado",
        paymentSectionRemove: "Remover",
        editFields: "Sugerir correção",
        cancel: "Cancelar",
        save: "Guardar",
        actionsTitle: "Ações",
        markPersonal: "Marcar como pessoal",
        unmarkPersonal: "Desmarcar pessoal",
        excludeFromPackage: "Excluir do pacote",
        includeInPackage: "Incluir novamente no pacote",
        requestRemoval: "Eliminar documento",
        // Stage 3B — send to accountant
        sendToAccountant: "Enviar ao contabilista",
        sendingToAccountant: "A enviar…",
        sentToAccountant: "Enviado ao contabilista",
        sendIneligible: "Este documento ainda não pode ser enviado ao contabilista.",
        sendNoAccountant: "Não existe uma ligação ativa com um contabilista para esta empresa.",
        removalReasonLabel: "Motivo (opcional)",
        confirmRemoval: "Eliminar documento",
        removalNote: "O documento será eliminado da sua caixa e deixará de ser incluído nos pacotes.",
        questionsTitle: "Perguntas de contexto",
        questionsAnswered: "Respondida",
        answerYes: "Sim",
        answerNo: "Não",
        answerSave: "Guardar resposta",
        duplicatesTitle: "Documentos parecidos",
        duplicatesOpen: "Comparar duplicados",
        duplicateConfirm: "Não é duplicado",
        duplicateMerge: "É o mesmo documento",
        markMissing: "Marcar sem talão",
        unmarkMissing: "Já tenho o talão",
        removalRequested: "Remoção pedida",
    },
    missingReceipt: {
        title: "Despesa sem talão",
        subtitle: "Adicione os dados que se lembrar. O seu contabilista vê que é manual.",
        supplier: "Fornecedor",
        supplierPh: "ex. Restaurante O Cantinho",
        date: "Data",
        amount: "Valor (€)",
        amountPh: "ex. 12,50",
        notes: "Notas (opcional)",
        notesPh: "Algo a explicar ao contabilista…",
        submit: "Adicionar à caixa de entrada",
        added: "Adicionada à sua caixa de entrada",
    },
    dashboard: {
        helloMorning: "Bom dia",
        helloAfternoon: "Boa tarde",
        helloEvening: "Boa noite",
        // Home-screen onboarding prompt (temporary, dismissible).
        // Settings keeps its own permanent install row — separate keys.
        installPrompt: {
            title: "Adicionar a Ordly ao ecrã principal",
            body: "Abra a Ordly mais rapidamente e capture documentos com um toque.",
            cta: "Como instalar",
            dismiss: "Agora não",
        },
        emptyTitle: "Comece o seu mês",
        emptyBody: "Tire uma foto de uma fatura ou talão. A Ordly trata da organização para o seu contabilista.",
        primary: "Tirar primeira foto",
        secondary: "Carregar documento",
        addDocument: "Adicionar documento",
        addPhoto: "Nova foto",
        recentTitle: "Recentes",
        seeAll: "Ver todos",
        readinessZero: "0% pronto",
        thisMonthLabel: "Este mês",
        viewInbox: "Caixa de entrada",
        inboxPendingLabel: "Pendentes",
        readinessRingLabel: "Pronto para o contabilista",
        recentActivityTitle: "Atividade recente",
        expectedTitle: "O que costuma chegar",
        expectedHint: "Marque o que já enviou. Veja o que falta.",
        monthsAllTitle: "Caixas mensais",
        monthDetailBack: "Voltar",
        statTotal: "Total",
        statReady: "Prontos",
        statPending: "A tratar",
        statPersonal: "Pessoal",
        statMissing: "Sem talão",
        statNeedsPhoto: "Precisa de foto",
        statToReview: "Por rever",
        amountTotal: "Total do mês",
        headlineReady: "{month} está pronto para o contabilista",
        headlinePartial: "{month} está {pct}% pronto para o contabilista",
        headlineEmpty: "{month} ainda sem documentos",
        readinessHelper: "Com base nos documentos adicionados até agora.",
        readinessHelperEmpty: "Adicione documentos para começar a preparar o mês.",
        monthSelectorAria: "Mudar mês",
        estimatedSummaryTitle: "Resumo estimado",
        estimatedTotalLabel: "Total estimado do mês",
        visibleVatLabel: "IVA visível",
        subjectToReview: "Sujeito a validação do contabilista",
        docsSavedTitle: "Documentos guardados",
        actionsTitle: "Ações",
        actionTakePhoto: "Tirar fotografia",
        actionTakePhotoHelper: "Capturar um recibo",
        actionUpload: "Carregar documento",
        actionUploadHelper: "Do seu dispositivo",
        actionScanMultiple: "Adicionar várias",
        actionScanMultipleHelper: "Várias faturas de uma vez",
        actionPreparePackage: "Preparar pacote",
        actionPreparePackageHelper: "Rever e enviar",
        needsAttentionTitleOne: "1 documento precisa da sua atenção",
        needsAttentionTitleMany: "{n} documentos precisam da sua atenção",
        needsAttentionBody: "{supplier} {reason} antes de seguir para o contabilista.",
        needsAttentionBodyNoSupplier: "Há documentos que {reason} antes de seguirem para o contabilista.",
        needsAttentionReasonContext: "precisa de contexto",
        needsAttentionReasonPhoto: "precisa de uma foto melhor",
        needsAttentionReasonDuplicate: "pode estar duplicado",
        needsAttentionReasonContextPlural: "precisam de contexto",
        needsAttentionReasonPhotoPlural: "precisam de uma foto melhor",
        needsAttentionReasonDuplicatePlural: "podem estar duplicados",
        needsAttentionCta: "Resolver agora",
        needsAttentionCrossTitleOne: "1 documento de outro mês precisa da sua atenção",
        needsAttentionCrossTitleMany: "{n} documentos de outros meses precisam da sua atenção",
        needsAttentionCrossBody: "{supplier} pertence a {month} e {reason}.",
        needsAttentionCrossBodyNoSupplier: "{n} documentos pertencem a meses anteriores e {reason}.",
        crossMonthUploadTitle: "Fatura guardada em {month}",
        crossMonthUploadBody: "A data da fatura indica {monthShort}, por isso guardámo-la nesse mês.",
        crossMonthUploadCtaView: "Ver {monthShort}",
        crossMonthUploadCtaOk: "Ok",
        batchCaptureTitle: "Adicionar várias",
        batchCaptureHint: "Carregue várias faturas de uma vez.",
        handoffPill: {
            noneTitle: "Contabilista ainda não ligado",
            noneCta: "Adicionar contabilista",
            notSentTitle: "Ainda não enviado este mês",
            notSentCta: "Enviar para contabilista",
            sentToday: "Pacote enviado hoje",
            sentYesterday: "Pacote enviado ontem",
            sentOnDate: "Último pacote enviado em {date}",
            sentRelative: "Última partilha: {month} · enviada há {n} {unit}",
            unitDay: "dia",
            unitDays: "dias",
            unitHour: "hora",
            unitHours: "horas",
            ariaOpenAccountant: "Abrir Contabilista",
        },
    },
    expectedDocs: {
        rent: "Renda",
        electricity: "Eletricidade",
        water: "Água",
        internet_mobile: "Internet/Telemóvel",
        accounting_fee: "Contabilidade",
        salaries: "Salários",
        fuel: "Combustível",
        subscriptions: "Subscrições",
        suppliers: "Fornecedores",
        other: "Outros",
    },
    months: {
        title: "Caixas mensais",
        all: "Todas",
        nothingYet: "Sem documentos neste mês ainda.",
        monthOf: "Mês de {label}",
        readiness: "Pronto",
        gotoCapture: "Capturar nova",
        markAsExpected: "Marcar como esperado",
        // Phase Month Detail visual refinement (Feb 2026)
        // The Month Detail hero is preparation-only ("revisto"), and is
        // semantically DIFFERENT from the Home/This Month hero copy
        // ("pronto para o contabilista"). We deliberately keep the two
        // wordings distinct so 100% reviewed cannot be misread as
        // "everything has been sent already".
        heroReviewedHeadlineReady: "{month} está revisto",
        heroReviewedHeadlinePartial: "{month} está {pct}% revisto",
        heroReviewedHeadlineEmpty: "{month} ainda sem documentos",
        heroReviewedHelper: "Com base nos documentos adicionados até agora.",
        heroReviewedHelperEmpty: "Adicione documentos para começar a preparar o mês.",
        // Compact under-hero counter strip
        countersDocs_one: "1 documento",
        countersDocs_other: "{n} documentos",
        countersToReview_one: "1 por rever",
        countersToReview_other: "{n} por rever",
        // Delivery card label (separate from "Resumo estimado")
        deliveryCardTitle: "Entrega ao contabilista",
        deliveryNoSendYet: "Ainda não enviou nada deste mês ao contabilista.",
        // Phase 15 — Meses as summary/history only
        notSentYet: "Ainda não enviado",
        estimatedTotal: "Total estimado",
        visibleVat: "IVA visível",
        statReady: "Prontos",
        statToReview: "Por rever",
        statMissing: "Sem talão",
        reviewPending: "Rever pendentes",
        sendViaAccountant: "Enviar documentos através de Contabilista",
        monthSummary: "Resumo do mês",
        // Month Handoff Status Refinement (Feb 2026)
        preparation: "Preparação",
        handoff: "Entrega ao contabilista",
        lastSend: "Último envio",
        notYetSent: "Ainda não enviado",
        viewSentDocs_one: "Ver documento enviado",
        viewSentDocs_other: "Ver documentos enviados",
        docsCount_one: "1 documento",
        docsCount_other: "{n} documentos",
        docsReviewed_one: "1 documento revisto",
        docsReviewed_other: "{n} documentos revistos",
        docsToReview_one: "1 por rever",
        docsToReview_other: "{n} por rever",
        docsSent_one: "1 enviado",
        docsSent_other: "{n} enviados",
        docsUnsent_one: "1 por enviar",
        docsUnsent_other: "{n} por enviar",
        newDocs_one: "1 novo documento",
        newDocs_other: "{n} novos documentos",
        newSinceLastSend_one: "1 novo documento desde o último envio",
        newSinceLastSend_other: "{n} novos documentos desde o último envio",
        readyForNextSend_one: "1 documento pronto para o próximo envio",
        readyForNextSend_other: "{n} documentos prontos para o próximo envio",
        readyForFirstSend_one: "1 pronto para o primeiro envio",
        readyForFirstSend_other: "{n} prontos para o primeiro envio",
        needsAttention_one: "1 documento precisa da sua atenção",
        needsAttention_other: "{n} documentos precisam da sua atenção",
        nothingToSend: "nada por enviar",
        organisedMonth: "Mês organizado",
        percentReviewed: "{pct}% revistos",
        // Hero labels
        heroPrepLabel: "Revisto",
        heroPrepLabelLong: "Documentos revistos",
        heroReadyForFirst: "Pronto para o primeiro envio",
        heroReadyNext: "Pronto para o próximo envio",
        heroOrganised: "Mês organizado",
        heroAttention: "Precisa da sua atenção",
        heroAttentionDocs_one: "1 documento por rever",
        heroAttentionDocs_other: "{n} documentos por rever",
        // Per-document delivery badges
        docBadge: {
            sent: "Enviado em {date}",
            new: "Novo",
            readyNext: "Pronto para o próximo envio",
            toReview: "Por rever",
        },
        // SendDetail screen
        send: {
            title: "Enviados em {date}",
            backToMonth: "Voltar ao mês",
            deliveryFailed: "Envio falhou",
            delivered: "Entregue",
            sent: "Enviado",
            opened: "Aberto",
            downloaded: "Descarregado",
            bounced: "Devolvido",
            complained: "Devolvido por reclamação",
            queued: "Enviado",
            unknownDelivery: "Estado desconhecido",
            empty: "Este envio não inclui nenhum documento deste mês.",
            loading: "A carregar envio…",
            notFound: "Envio não encontrado.",
        },
    },
    bottomNav: {
        home: "Início",
        inbox: "Inbox",
        capture: "Capturar",
        months: "Meses",
        accountant: "Contabilista",
        settings: "Definições",
    },
    accountantHandoff: {
        eyebrow: "Entrega Ordly",
        title: "Contabilista",
        subtitle: "Adicione o seu contabilista e envie pacotes de documentos com segurança.",
        emptyTitle: "Adicionar contabilista",
        emptyHelper: "Para enviar o mês organizado, adicione o email do contabilista.",
        saveHelper: "Adicione pelo menos o email e um nome ou gabinete.",
        reassurance: "Pode adicionar ou alterar o contabilista mais tarde.",
        nameLabel: "Nome do contabilista",
        firmLabel: "Gabinete de contabilidade",
        emailLabel: "Email do contabilista",
        phoneLabel: "Telefone (opcional)",
        handoffRhythm: "Preferência de envio",
        rhythmMonthly: "Mensal",
        rhythmWeekly: "Semanal",
        rhythmOnRequest: "A pedido",
        saveAccountant: "Guardar contabilista",
        editConnection: "Editar",
        currentPackage: "Pacote atual",
        packageMonthFmt: "Pacote de {monthName}",
        summaryLoadError: "Não foi possível carregar o pacote. Tente novamente.",
        summaryRetryCta: "Tentar novamente",
        summaryRetrying: "A tentar...",
        statusReady: "Pronto para envio",
        statusNeedsReview: "Precisa de atenção",
        statusEmpty: "Sem documentos",
        readyDocuments: "documentos prontos",
        needContext: "precisam de contexto",
        possibleDuplicates: "possíveis duplicados",
        sendPackage: "Enviar para contabilista",
        sending: "A enviar…",
        resolveBeforeSending: "Resolva os documentos pendentes antes de enviar.",
        emptyPeriod: "Ainda não há documentos neste período.",
        lastHandoff: "Último envio",
        lastHandoffFmt: "Pacote de {period} enviado para {email} em {date}",
        // Phase Month-Send-Unsent (Feb 2026) — Mais opções + new states
        alreadySent: "já enviados",
        alreadySentOne: "já enviado",
        unsentDocs: "documentos por enviar",
        unsentDocsOne: "documento por enviar",
        everythingSent: "Tudo enviado",
        viewLastSend: "Ver último envio",
        moreOptions: "Mais opções",
        sendFullMonth: "Enviar mês completo",
        viewPreviousSends: "Ver envios anteriores",
        confirmFullMonthTitle: "Enviar o mês completo?",
        confirmFullMonthBody: "Este pacote incluirá {n} documentos:",
        confirmFullMonthAlreadySent: "{n} já enviado anteriormente",
        confirmFullMonthAlreadySentPlural: "{n} já enviados anteriormente",
        confirmFullMonthNew: "{n} ainda não enviado",
        confirmFullMonthNewPlural: "{n} ainda não enviados",
        confirmFullMonthFooter: "Será criado um novo pacote e um novo link seguro.",
        confirmFullMonthCta: "Enviar mês completo",
        sendErrorNoUnsent: "Não há documentos novos para enviar neste mês.",
        previousSendsTitle: "Envios anteriores de {monthName}",
        previousSendsEmpty: "Ainda não há envios anteriores deste mês.",
        previousSendsClose: "Fechar",
        previousSendOneDoc: "{n} documento",
        previousSendDocsPlural: "{n} documentos",
        viewPackage: "Ver pacote",
        resendPackage: "Reenviar",
        confirmResendTitle: "Reenviar este pacote?",
        confirmResendBody: "Será enviado novamente para:",
        confirmResendCta: "Reenviar pacote",
        confirmResendCancel: "Cancelar",
        resendHistoricalSuccess: "Pacote reenviado.",
        // Phase 12c — Resend webhook delivery pill
        delivery: {
            queued: "Email enviado",
            delivered: "Email entregue",
            bounced: "Email devolvido",
            complained: "Reclamação recebida",
            failed: "Email falhou",
            unknown: "Estado desconhecido",
        },
        deliveryWarning: "Verifique o email do contabilista ou envie novamente.",
        // Phase 12d — manual resend action
        complaintHelper: "O destinatário marcou este email como spam. Contacte-o antes de enviar novamente.",
        resendHelper: "Verifique o email do contabilista antes de reenviar.",
        resendCta: "Reenviar email",
        resendSending: "A reenviar…",
        resendSuccess: "Email reenviado.",
        resendFailed: "Não foi possível reenviar. Tente novamente.",
        resendCooldown: "Aguarde {seconds}s antes de reenviar.",
        resendNotApplicable: "Não é possível reenviar a partir do estado atual.",
        securityNote: "Apenas os documentos incluídos no pacote enviado ficam disponíveis através do link seguro.",
        confirmTitle: "Enviar pacote de {monthName}?",
        confirmBody: "A Ordly vai enviar um link seguro para {email} com os documentos prontos deste período.",
        confirmCta: "Enviar pacote",
        confirmCancel: "Cancelar",
        sentToastTitle: "Pacote enviado",
        sentToastBody: "Link seguro enviado para {email}.",
        sendErrorTitle: "Não foi possível enviar o pacote. Tente novamente.",
        sendErrorBody: "Verifique a sua ligação e tente outra vez. Se o problema persistir, contacte o suporte.",
        months: {
            "01": "janeiro", "02": "fevereiro", "03": "março", "04": "abril",
            "05": "maio",   "06": "junho",     "07": "julho", "08": "agosto",
            "09": "setembro","10": "outubro",  "11": "novembro","12": "dezembro",
        },
    },

    activityFeed: {
        document_uploaded: "Fatura adicionada",
        document_processed: "Documento processado",
        document_processing_started: "A processar documento",
        document_processing_failed: "Falha a processar documento",
        document_edited: "Documento atualizado",
        document_action_mark_personal: "Marcado como pessoal",
        document_action_unmark_personal: "Desmarcado pessoal",
        document_action_toggle_missing_receipt: "Despesa sem talão atualizada",
        document_action_include_in_package: "Incluído no pacote",
        document_action_exclude_from_package: "Excluído do pacote",
        document_action_answer_question: "Documento precisa de contexto",
        document_removal_requested: "Documento removido",
        document_retry_requested: "Nova tentativa pedida",
        missing_receipt_created: "Despesa sem talão adicionada",
        package_created: "Pacote preparado",
        package_generated: "Pacote gerado",
        package_sent: "Pacote enviado ao contabilista",
        package_opened: "Contabilista abriu o pacote",
        package_downloaded: "Contabilista descarregou o pacote",
        package_revoked: "Pacote revogado",
        package_regenerated: "Pacote regenerado",
        fallback: "Atividade",
    },
    packages: {
        title: "Pacote para o contabilista",
        listTitle: "Pacotes enviados",
        empty: "Ainda não enviou nenhum pacote.",
        createForMonth: "Preparar pacote do mês",
        createForThisMonth: "Preparar pacote deste mês",
        backToMonth: "Voltar ao mês",
        backToPackages: "Voltar aos pacotes",
        loading: "A preparar pacote...",
        generating: "A gerar ficheiros...",
        sending: "A enviar...",
        revoking: "A revogar...",
        regenerating: "A regenerar...",
        steps: {
            review: "Rever",
            generate: "Gerar",
            send: "Partilhar",
        },
        state: {
            draft: "Rascunho",
            ready: "Pronto a enviar",
            sent: "Enviado",
            opened: "Aberto pelo contabilista",
            downloaded: "Descarregado",
            expired: "Expirado",
            revoked: "Revogado",
            superseded: "Substituído",
        },
        review: {
            heading: "Rever o pacote",
            documents: "{n} documento incluído",
            documentsPlural: "{n} documentos incluídos",
            amountTotal: "Total do mês (excl. pessoais)",
            included: "Incluído",
            personal: "Pessoal",
            missing: "Sem talão",
            empty: "Não há documentos prontos para este mês ainda.",
        },
        generate: {
            heading: "Gerar ficheiros do pacote",
            description: "Vamos preparar o ZIP com as imagens, o metadata.csv, o context_answers.csv e o summary.txt.",
            cta: "Gerar pacote",
            zipSize: "ZIP {size}",
        },
        send: {
            heading: "Partilhar com o contabilista",
            description: "Geramos um link seguro válido por 14 dias. O contabilista não precisa de criar conta.",
            accountantName: "Nome do contabilista",
            accountantNamePh: "ex.: Maria Silva",
            accountantEmail: "Email do contabilista",
            accountantEmailPh: "maria@contabilidade.pt",
            note: "Nota (opcional)",
            notePh: "Algum pormenor que ele deva saber sobre este mês?",
            cta: "Gerar link e enviar",
            ctaResend: "Gerar novo link",
            successTitle: "Link pronto",
            successHint: "Copie e envie ao contabilista, por email, WhatsApp ou onde preferir.",
            copy: "Copiar link",
            copied: "Copiado!",
            expiresOn: "Expira a {date}",
        },
        share: {
            link: "Link de partilha",
            tokenSuffix: "termina em …{suffix}",
            revokedHint: "Este link foi revogado.",
            expiredHint: "Este link expirou.",
            supersededHint: "Foi substituído por um pacote mais recente.",
        },
        actions: {
            revoke: "Revogar link",
            revokeConfirm: "Tem a certeza? O contabilista deixa de poder aceder.",
            regenerate: "Regenerar pacote",
            regenerateConfirm: "Vai criar um pacote novo. O atual fica substituído.",
            download: "Descarregar (eu)",
        },
        events: {
            title: "Histórico",
            empty: "Ainda sem eventos.",
            created: "Pacote criado",
            generated: "Ficheiros gerados",
            sent: "Link enviado",
            opened: "Aberto pelo contabilista",
            downloaded: "Descarregado pelo contabilista",
            revoked: "Link revogado",
            regenerated: "Pacote regenerado",
            expired: "Link expirou",
        },
        meta: {
            sentAt: "Enviado",
            openedAt: "Aberto",
            downloadedAt: "Descarregado",
            opens: "Aberturas",
            downloads: "Descarregamentos",
            expiresIn: "Expira em {days} dias",
            expiresToday: "Expira hoje",
            expiredOn: "Expirou a {date}",
        },
    },
    accountant: {
        // Public landing page (no auth)
        title: "Pacote para o contabilista — {month} {year}",
        from: "Empresa",
        sentBy: "{company} partilhou este pacote consigo através da Ordly.",
        sentAt: "Enviado a {date}",
        sentAtLabel: "Enviado em",
        validUntilLabel: "Link válido até",
        referenceLabel: "Referência",
        expiresIn: "Expira em {days} dias",
        contents: "Conteúdo",
        documents: "{n} documento",
        documentsPlural: "{n} documentos",
        amountTotal: "Total (excl. pessoais)",
        download: "Descarregar pacote (.zip)",
        downloading: "A descarregar...",
        downloaded: "Descarregado",
        notFound: "Este pacote não está disponível.",
        revoked: "Este link foi revogado pela empresa.",
        expired: "Este link expirou. Peça à empresa um novo link.",
        notReady: "Este pacote ainda não está pronto.",
        zipContents: {
            heading: "O pacote inclui:",
            summary: "Resumo mensal em PDF",
            metadata: "Dados dos documentos em CSV",
            context: "Respostas de contexto em CSV",
            documents: "{n} documento original",
            documentsPlural: "{n} documentos originais",
        },
        attribution: "Preparado com Ordly",
        learnMore: "Saber mais sobre a Ordly",
        notePrefix: "Nota da empresa:",
        secureBadge: "Link seguro e privado",
        secureValidUntil: "Válido até {date}",
        secureLineExpired: "Link expirou",
        amountDash: "—",
        docPreview: {
            open: "Abrir documento",
            openInNewTab: "Abrir num separador novo",
            close: "Fechar",
            cannotRender: "O navegador não conseguiu apresentar este documento.",
            loading: "A carregar pré-visualização...",
        },
    },
    settings: {
        title: "Definições",
        subtitle: "A sua conta e a equipa Ordly",
        role: {
            owner: "Dono",
            manager: "Gestor",
            captureOnly: "Apenas captura",
            member: "Membro",
        },
        language: {
            title: "Idioma",
            hint: "Toda a aplicação fica nesta língua, incluindo o pacote para o contabilista.",
        },
        pwa: {
            // Settings card (Feb 2026 refinement — no offline claim,
            // no native alert, dedicated /install route).
            title: "Adicionar ao ecrã principal",
            hint: "Abra a Ordly mais rapidamente, como uma app.",
            installCta: "Como instalar",
        },
        installGuide: {
            // Dedicated /install screen
            title: "Adicionar a Ordly ao ecrã principal",
            intro: "Abra a Ordly mais rapidamente e capture documentos com um toque.",
            iphone: "iPhone",
            android: "Android",
            ios: {
                step1Title: "Toque em Partilhar",
                step1Body: "No Safari, toque no botão Partilhar.",
                step2Title: "Escolha \u201cAdicionar ao ecrã principal\u201d",
                step2Body: "Desça no menu e selecione \u201cAdicionar ao ecrã principal\u201d.",
                step3Title: "Confirme em \u201cAdicionar\u201d",
                step3Body: "A Ordly ficará disponível no ecrã principal do seu iPhone.",
            },
            androidSteps: {
                step1Title: "Abra o menu do navegador",
                step1Body: "Toque no menu no canto superior do navegador.",
                step2Title: "Escolha \u201cAdicionar ao ecrã principal\u201d",
                step2Body: "Em alguns dispositivos poderá aparecer \u201cInstalar aplicação\u201d.",
                step3Title: "Confirme a instalação",
                step3Body: "A Ordly ficará disponível no ecrã principal do seu telemóvel.",
            },
            done: "Concluir",
            back: "Voltar",
        },
        comingSoon: {
            title: "Em breve",
            team: "Convidar a equipa",
            permissions: "Gerir permissões",
            invite: "Convidar contabilista",
            notifications: "Notificações",
            tag: "Em breve",
        },
        signOut: "Terminar sessão",
        signOutHelper: "Sair desta conta neste dispositivo.",
        backHome: "Voltar a Este mês",
        demoTag: "DEMO",
        company: {
            title: "Empresa",
            edit: "Editar",
            save: "Guardar",
            cancel: "Cancelar",
            name: "Nome",
            nif: "NIF",
            nifLocked: "Não editável",
            nifLockHint: "O NIF é fixo após o registo para manter o histórico de documentos coerente.",
            businessType: "Tipo de atividade",
            businessTypePh: "ex.: Estúdio de design",
            currency: "Moeda",
            savedToast: "Dados da empresa atualizados.",
            errorToast: "Não consegui guardar — verifique os dados.",
        },
        frequency: {
            title: "Frequência de envio",
            hint: "Com que frequência partilha pacotes com o contabilista",
            opts: {
                monthly: "Mensal",
                biweekly: "Quinzenal",
                manual: "Apenas manual",
            },
            optDesc: {
                monthly: "Aviso no início de cada mês",
                biweekly: "Aviso a cada 14 dias",
                manual: "Sem avisos automáticos",
            },
        },
        reminders: {
            title: "Avisos",
            hint: "Os avisos são simulados — nenhum email real é enviado ainda.",
            nextAt: "Próximo aviso: {date}",
            manualOnly: "Sem próximos avisos automáticos.",
            simulateCta: "Simular aviso (DEMO)",
            simulatedToast: "Aviso simulado — nada foi enviado.",
            savedToast: "Frequência atualizada.",
        },
        inbox: {
            title: "Reencaminhar para a sua Ordly",
            hint: "Reencaminhe faturas para este endereço — quando ligarmos o intake real, vão aparecer automaticamente na inbox. Por agora é simulado.",
            copy: "Copiar",
            copiedToast: "Endereço copiado.",
            simulateCta: "Simular reencaminhamento (DEMO)",
            simulatedToast: "Reencaminhamento simulado — nada foi processado.",
        },
    },
    demo: {
        label: "DADOS DEMO",
        emailSimulated: "Email simulado em ambiente demo",
        ocrMock: "OCR DEMO",
        valuesEstimated: "Valores estimados",
    },
    emailIntake: {
        title: "Receba faturas por email",
        helper: "Encaminhe as suas faturas para:",
        loadingAddress: "A carregar endereço…",
        copy: "Copiar endereço",
        share: "Partilhar",
        copied: "Endereço copiado",
        copyFailed: "Não foi possível copiar. Selecione e copie manualmente.",
        processingHint: "Os anexos compatíveis serão adicionados automaticamente à sua Inbox.",
        howTitle: "Como funciona",
        howStep1: "Encaminhe o email com a fatura",
        howStep2: "A Ordly processa os anexos",
        howStep3: "Reveja os dados na Inbox",
        supportedTitle: "Anexos suportados",
        supportedBody: "PDF, JPG, PNG, HEIC ou WebP · até 20 MB por ficheiro",
        supportedPasswordNote: "PDFs protegidos por palavra-passe não podem ser processados.",
        noSupportedAttachmentDetail: "Não encontrámos nenhum anexo compatível neste email.",
        recentTitle: "Atividade recente",
        recentEmpty: "Ainda não recebeu nenhuma fatura por email.",
        viewAllInbox: "Ver todos na Inbox",
        aliasMissing: "O envio por email ainda não está ativo para esta empresa.",
        unavailable: "Esta funcionalidade ainda não está disponível para esta empresa.",
        unavailableDetail: "Quando estiver ativa, poderá encaminhar faturas para um endereço exclusivo da Ordly.",
        backToCapture: "Voltar a Capturar",
        attachmentsOne: "1 anexo",
        attachmentsMany: "{n} anexos",
        attachmentsZero: "Sem anexos",
        senderUnknown: "Remetente desconhecido",
        eventState: {
            processing: "A processar anexos",
            processed: "Processado",
            partially_processed: "Processado parcialmente",
            no_supported_attachments: "Sem faturas compatíveis",
        },
    },
    employee: {
        greeting: "Olá, {name}",
        myExpenses: "As minhas despesas",
        monthLabel: "{month}",
        documentsLabel: "{count} documentos",
        identifiedAmount: "{amount} identificados",
        personallyPaid: "{amount} pagos pessoalmente",
        needsAttention: "{count} precisa da sua atenção",
        needsAttentionPlural: "{count} precisam da sua atenção",
        captureCta: "Tirar fotografia",
        uploadCta: "Carregar documento",
        recentDocuments: "Últimos documentos",
        empty: "Ainda não enviou nenhum documento.",
        incompleteNote: "Com base nos valores identificados até agora.",
        nav: {
            home: "Início",
            capture: "Capturar",
            documents: "Os meus documentos",
        },
        status: {
            saved: "Guardado",
            processing: "A processar",
            ready: "Pronto",
            needs_attention: "Precisa da sua atenção",
            possible_duplicate: "Possível duplicado",
            awaiting_owner_review: "A aguardar revisão da empresa",
            included_in_next_send: "Incluído no próximo envio",
            shared_with_accountant: "Já partilhado com a contabilidade",
        },
        detail: {
            title: "Documento",
            note: "Nota",
            notePlaceholder: "Detalhe rápido para o responsável da empresa.",
            paymentMethod: "Método de pagamento",
            amount: "Valor",
            date: "Data do documento",
            supplier: "Fornecedor",
            save: "Guardar",
            saved: "Guardado.",
            saveError: "Não foi possível guardar.",
            retry: "Tentar novamente",
            remove: "Apagar",
            confirmRemove: "Apagar este documento?",
            packageLocked:
                "Já partilhado com a contabilidade. Fale com o responsável da empresa para qualquer correção.",
            paymentMethods: {
                company_card: "Cartão da empresa",
                company_bank_account: "Conta bancária da empresa",
                cash: "Dinheiro",
                personal_method: "Método pessoal",
                unpaid: "Por pagar",
                unset: "Não indicado",
            },
        },
        capture: {
            title: "Novo documento",
            takePhoto: "Tirar fotografia",
            chooseFile: "Escolher ficheiro",
            note: "Nota (opcional)",
            notePlaceholder: "Ex.: almoço com cliente",
            send: "Enviar",
            sending: "A enviar…",
            success: "Documento enviado.",
            error: "Não foi possível enviar o documento.",
            tooLarge: "Ficheiro demasiado grande (máx 20MB).",
            // Step labels & UI states for the parity flow.
            paymentMethodLabel: "Método de pagamento",
            paymentMethodHelp: "Opcional — pode confirmar depois.",
            attached: "Anexado",
            replace: "Substituir",
            // Status screens
            optimizing: "A otimizar a imagem…",
            uploading: "A enviar o documento…",
            processing: "A ler o documento…",
            processingHelp: "Pode demorar alguns segundos.",
            timedOut: "Demorou mais do que o esperado. Pode continuar a aguardar ou tentar novamente.",
            // QR Assist (pre-upload, neutral wording)
            qrPromptTitle: "Código QR não detetado automaticamente",
            qrPromptBody: "Se o documento tiver um código QR, pode tirar uma fotografia mais próxima para ajudar a Ordly a ler a informação.",
            qrPromptTakeQr: "Fotografar código QR",
            qrPromptContinue: "Continuar sem QR",
            qrPromptRetake: "Tirar outra fotografia ao documento",
            qrAssistAttached: "Foto do código QR anexada.",
            // Confirmation
            confirmTitle: "Confirme a informação",
            confirmHelp: "Verifique e complete o que estiver em falta.",
            confirmFieldSupplier: "Fornecedor",
            confirmFieldDate: "Data do documento",
            confirmFieldAmount: "Total",
            confirmFieldPayment: "Pago com",
            confirmFieldNote: "Nota",
            // Phase 17 (Jul 2026) — Employee post-capture context.
            // Independent of ``employee_note``; saves ONLY
            // ``document_context_note`` via the scoped employee PATCH.
            confirmFieldContext: "Adicionar contexto",
            confirmFieldContextHelper: "Opcional — será partilhado com a empresa e o contabilista.",
            confirmFieldContextPlaceholder: "Ex.: refeição com cliente, deslocação, material para o escritório…",
            confirmFieldContextSave: "Guardar contexto",
            confirmFieldContextSaved: "Contexto guardado",
            confirmFieldContextError: "Não foi possível guardar o contexto. Tente novamente.",
            confirmMissing: "Por preencher",
            confirmLocked: "Lido do código QR",
            confirmCta: "Confirmar e enviar",
            confirmSaving: "A guardar…",
            confirmSaveError: "Não foi possível guardar as alterações.",
            // Duplicate
            duplicateTitle: "Este documento pode já ter sido enviado.",
            duplicateBody: "A empresa decide o tratamento final. Pode confirmar à mesma se quiser submeter.",
            duplicateConfirm: "Submeter mesmo assim",
            // Unreadable
            unreadableTitle: "Não conseguimos ler este documento.",
            unreadableBody: "Tire outra fotografia com o documento inteiro, bem iluminado e sem reflexos.",
            unreadableRetake: "Tirar outra fotografia",
            // Generic retry
            retry: "Tentar novamente",
        },
        revoked: {
            title: "Este acesso já não está ativo.",
            body: "Peça um novo link ao responsável da empresa.",
        },
        loading: "A carregar…",
        homeScreen: {
            title: "Adicionar ao ecrã principal",
            iosStep1: "Toque em Partilhar",
            iosStep2: "Escolha “Adicionar ao ecrã principal”",
            iosStep3: "Toque em Adicionar",
            androidBody:
                "Abra o menu do navegador e escolha “Adicionar ao ecrã principal”.",
            dismiss: "Mais tarde",
            cta: "Como adicionar",
        },
    },
    ownerEmployees: {
        // — Settings entry point —
        settingsRow: "Convidar a equipa",
        // — Page —
        title: "Equipa",
        add: "Adicionar colaborador",
        empty: "Ainda não existem colaboradores",
        emptyHint: "Convide alguém para enviar e acompanhar os seus próprios documentos da empresa.",
        docsLine: "{count} documentos · {amount} identificados",
        personalLine: "{amount} pagos pessoalmente",
        needsLine: "{count} precisa de atenção",
        status: {
            active: "Ativo",
            revoked: "Revogado",
        },
        create: {
            title: "Adicionar colaborador",
            nameLabel: "Nome do colaborador",
            nameHelper: "Receberá um link privado para enviar e acompanhar os seus documentos.",
            namePlaceholder: "Maria Silva",
            submit: "Criar acesso",
            cancel: "Cancelar",
            error: "Não foi possível criar o acesso. Tente novamente.",
        },
        reveal: {
            title: "Acesso criado",
            body: "Partilhe este acesso privado com {name}.",
            warning: "O link é apresentado apenas uma vez.",
            copy: "Copiar link",
            copied: "Link copiado",
            whatsapp: "WhatsApp",
            showQr: "Mostrar QR",
            hideQr: "Ocultar QR",
            qrCaption: "QR pessoal de {name}. Não partilhe com outras pessoas.",
            done: "Concluir",
            close: "Fechar",
            whatsappBody:
                "Olá, {name}. Este é o teu acesso à Ordly para enviares e acompanhares os teus documentos da empresa:\n\n{link}\n\nAbre o link no telemóvel e adiciona-o ao ecrã principal para teres acesso mais rápido.",
        },
        detail: {
            nameLabel: "Nome",
            editName: "Editar",
            saveName: "Guardar",
            cancelEdit: "Cancelar",
            replaceLink: "Substituir link",
            revoke: "Remover da equipa",
            replaceConfirmTitle: "Substituir link de acesso?",
            replaceConfirmBody:
                "O link anterior e todas as sessões ativas deixarão de funcionar imediatamente.",
            replaceConfirm: "Substituir",
            revokeConfirmTitle: "Remover {name} da equipa?",
            revokeConfirmBody:
                "{name} deixará de ter acesso à Ordly e será removido da lista da equipa.\n\nOs documentos enviados anteriormente e o respetivo histórico continuarão disponíveis.",
            revokeConfirm: "Remover da equipa",
            docsTitle: "Documentos enviados ({count})",
            empty: "Ainda não enviou nenhum documento.",
            documentFallback: "Documento",
        },
    },
    ownerReview: {
        attribution: "Enviado por {name}",
        attributionUnknown: "Enviado por colaborador",
        awaitingReview: "A aguardar revisão da empresa",
        includedInNextSend: "Incluído no próximo envio",
        sharedWithAccountant: "Já partilhado com a contabilidade",
        includeCta: "Incluir no próximo envio",
        includeSuccess: "Incluído no próximo envio.",
        includeError: "Não foi possível incluir.",
    },
    companyBrand: {
        poweredByOrdly: "Com tecnologia Ordly",
        relationship: {
            managedBy: "Gerido pelo contabilista",
            bannerAria: "Ver detalhes do contabilista",
            accountantBlock: "O seu contabilista é {firm}. Os documentos partilhados são tratados pela equipa {firm}.",
        },
    },
};

const en = {
    common: {
        appName: "Ordly",
        loading: "Loading…",
        save: "Save",
        cancel: "Cancel",
        continue: "Continue",
        back: "Back",
        skip: "Skip for now",
        next: "Next",
        signIn: "Sign in",
        signOut: "Sign out",
        createWorkspace: "Create workspace",
        languagePT: "Português",
        languageEN: "English",
        errorGeneric: "We couldn't process your request. Please try again.",
        supplierNifFallback: "Supplier NIF {nif}",
    },
    splash: { tagline1: "Smarter operations.", tagline2: "Simpler workflows." },
    chooseWorkspace: {
        title: "Choose your workspace",
        subtitle: "Ordly serves both companies and accountants. Pick where you want to go in — you can switch at any time.",
        company: {
            title: "Company Workspace",
            description: "For businesses and employees managing expenses and documents.",
            cta: "Continue to Company Workspace",
        },
        accountant: {
            title: "Accountant Workspace",
            description: "For accounting firms managing clients, teams and exceptions.",
            cta: "Continue to Accountant Workspace",
        },
        switchToAccountantPrompt: "Are you an accountant?",
        switchToAccountantCta: "Go to Accountant Workspace",
    },
    accountantInvite: {
        loading: "Loading invitation…",
        readyTitle: "Accountant invitation",
        readyBody: "Review the firm's identity and accept or reject this invitation. Ordly has not shared any of your Company's data before your acceptance.",
        firmLabel: "Firm",
        companyLabel: "Company",
        expiresLabel: "Expires",
        onlyOwnerHint: "Only an active verified Company owner can accept or reject this invitation.",
        acceptButton: "Accept and connect",
        acceptingButton: "Accepting…",
        rejectButton: "Reject",
        rejectingButton: "Rejecting…",
        acceptedTitle: "Invitation accepted",
        acceptedBody: "Your connection with the accounting firm is now active.",
        rejectedTitle: "Invitation rejected",
        rejectedBody: "We've let the accounting firm know.",
        continue: "Continue to my workspace",
        backToWorkspace: "Back to my workspace",
        errorTitle: "Cannot open invitation",
        error: {
            generic: "Something went wrong. Try again or contact the firm.",
            invitation_not_found: "This invitation could not be found or is no longer available.",
            invitation_already_resolved: "This invitation has already been processed.",
            invitation_expired: "This invitation has expired.",
            only_owner_may_accept: "Only an active verified Company owner can accept this invitation.",
            only_owner_may_reject: "Only an active verified Company owner can reject this invitation.",
            email_not_verified: "Please verify your email first before accepting this invitation.",
            company_already_connected: "Your Company is already connected to another accounting firm.",
        },
    },
    sponsorshipInvite: {
        loading: "Loading invitation…",
        // Stage 5E.2 Phase 6 — new locked copy (contract §21.1).
        readyTitle: "Activate your workspace",
        readyBody: "Your accounting firm has prepared this workspace for you. Review and activate to get started.",
        // Stage 5D keys retained one release for rollback per §21.1.
        readyTitleV1: "Activate Ordly",
        readyBodyV1: "Your accounting firm set up an Ordly workspace for you. Review and activate to get started.",
        firmLabel: "Firm",
        clientLabel: "Company",
        recipientLabel: "Email",
        expiresLabel: "Expires",
        brand: {
            // Phase 6 (new): firm-led, no "to Ordly" framing.
            brandedHeading: "{firm} has prepared your client workspace",
            brandedBody: "{firm} has prepared {client} for you.",
            // Stage 5D legacy retained one release.
            brandedHeadingV1: "{firm} has invited you to Ordly",
            logoAlt: "{firm} logo",
            poweredByOrdly: "Powered by Ordly · getordly.app",
        },
        signInPromptTitle: "Already have an Ordly account?",
        signInPromptBody: "Sign in to continue.",
        signInButton: "Sign in and continue",
        createAccountTitle: "Create your account",
        createAccountBody: "Set a password to activate this workspace.",
        fullNameLabel: "Full name",
        passwordLabel: "Password",
        // Phase 6: CTA per contract §21.1.
        acceptButton: "Activate your workspace",
        acceptButtonV1: "Activate and enter",
        acceptingButton: "Activating…",
        rejectButton: "Reject",
        rejectingButton: "Rejecting…",
        // Phase 6: success copy per contract §21.1.
        acceptedActiveTitle: "Your workspace is ready",
        acceptedActiveTitleV1: "Workspace activated",
        acceptedActiveBody: "Your client workspace is ready. Redirecting you now.",
        acceptedPendingTitle: "Waiting for confirmation",
        acceptedPendingBody: "A company with this VAT number already exists on Ordly. We've asked its owner to confirm the connection.",
        rejectedTitle: "Invitation rejected",
        rejectedBody: "We've let the accounting firm know.",
        continue: "Continue",
        backToLogin: "Go to sign in",
        errorTitle: "Cannot open invitation",
        error: {
            generic: "Something went wrong. Try again or contact the firm.",
            sponsorship_invitation_not_found: "This invitation could not be found or is no longer available.",
            sponsorship_invitation_already_resolved: "This invitation has already been processed.",
            sponsorship_invitation_expired: "This invitation has expired.",
            recipient_email_mismatch: "This invitation was sent to a different email address.",
            authentication_required: "Please sign in to continue.",
            ownership_verification_required: "A company with this VAT number already exists. We'll ask its owner to confirm.",
            sponsored_slot_exhausted: "The firm has reached its sponsored workspace limit. Please contact your accountant.",
            company_already_connected: "This Company is already connected to another firm.",
            relationship_already_exists_same_workspace: "A connection request is already in progress for this Company.",
            nif_requires_manual_resolution: "Multiple companies exist with this VAT number. Please contact your accountant.",
            missing_credentials: "Please provide a name and password to activate.",
        },
    },
    welcome: {
        headline: "Capture expenses.",
        headline2: "Hand off the month organized.",
        intro: "Save receipts and invoices throughout the month. Ordly organizes everything into an accountant-ready package.",
        primary: "Create workspace",
        secondary: "Sign in",
        support: "Start now. Invite your accountant when you are ready.",
    },
    landing: {
        heroPill: "For small businesses in Portugal",
        heroTitle: "No more shoebox expenses.",
        heroSub:
            "Capture receipts and invoices throughout the month. Ordly organizes everything and prepares a clean package for your accountant.",
        ctaPrimary: "Start organizing expenses",
        ctaSecondary: "I already have an account",
        support: "Start alone. Connect your accountant when ready.",
        featuresTitle: "Everything you need to keep the month ready",
        f1Title: "Take a photo in seconds",
        f1Desc: "No forms. Ordly processes the document afterwards.",
        f2Title: "Paper receipts, in batch",
        f2Desc: "Scan many paper receipts in a single flow.",
        f3Title: "Catches blurry and duplicates",
        f3Desc: "Before they become a problem for your accountant.",
        f4Title: "See if your month is ready",
        f4Desc: "A clear percentage, no stress.",
        f5Title: "Clean package for your accountant",
        f5Desc: "ZIP, CSV and summary — shared via secure link.",
        f6Title: "Your accountant validates accounting",
        f6Desc: "Ordly prepares context. You stay in control.",
        safetyLine: "Ordly prepares documents and context. Your accountant validates the accounting.",
        footerNote: "Ordly · Documents and expenses for small businesses in Portugal.",
    },
    auth: {
        registerTitleL1: "Create your",
        registerTitleL2: "company workspace",
        registerTitle: "Create your company workspace",
        createWorkspaceTitle: "Create your\ncompany workspace",
        createWorkspaceSubtitle: "Organize invoices and receipts. Save time, stay compliant, and keep your business ready.",
        createWorkspace: "Create workspace",
        registerSubtitle: "Organize invoices and receipts. Save time, stay compliant, and keep your business ready.",
        promise: "Take a photo. Keep the month ready.",
        promisePill: "Take a photo. Keep the month ready.",
        welcomeBack: "Welcome back",
        loginTitle: "Welcome back",
        loginSubtitle: "Sign in to stay on top of your documents, deadlines, and month-end readiness.",
        signIn: "Sign in",
        sendMagicLink: "Send magic link",
        alreadyHaveAccount: "Already have an account?",
        dontHaveAccount: "Don't have an account?",
        companyName: "Company name",
        companyNamePh: "e.g. Ordly Solutions, Lda.",
        nif: "NIF",
        nifPh: "e.g. 500 123 456",
        nifRequired: "Company VAT number is required.",
        nifInvalid: "The VAT number entered does not look valid. Please check the 9 digits.",
        nifPersonalNotice: "This VAT number appears to belong to an individual. If you work as a sole trader/freelancer, you can continue.",
        businessType: "Business type",
        businessTypePh: "Select your business type",
        email: "Email",
        emailPh: "e.g. hello@ordly.com",
        password: "Password",
        passwordPh: "Create a strong password",
        passwordPhLogin: "Enter your password",
        rememberDevice: "Remember this device",
        forgotPassword: "Forgot password?",
        magicLink: "Send magic link",
        magicLinkSentTitle: "Link sent",
        magicLinkSentBody: "Check your email. The link expires in 15 minutes.",
        magicLinkDemo: "Simulated email in demo. Use the token below to sign in:",
        magicLinkConsume: "Sign in with link",
        noAccount: "Don't have an account?",
        hasAccount: "Already have an account?",
        createOne: "Create one",
        signInLink: "Sign in",
        pilotBadge: "Private pilot",
        pilotHelp: "Ordly is currently available by invitation only.",
        legal: "By creating an account, you agree to our Terms and Privacy Policy. Your documents are private and encrypted.",
        termsAcceptPrefix: "I agree to the ",
        termsLink: "Terms",
        termsAcceptAnd: " and ",
        privacyLink: "Privacy Policy",
        termsAcceptSuffix: ".",
        termsRequired: "Please accept the Terms and Privacy Policy to create your account.",
        serviceUnavailable: "We're having temporary trouble. Please try again in a few seconds.",
        trustNote: "Your data is secure. We use industry-standard encryption to protect your information.",
        registerCTA: "Create workspace",
        loginCTA: "Sign in",
        or: "or",
        // Phase 11 — email verification
        verifyEmailTitle: "Confirm your email",
        verifyEmailHelper: "We sent a 6-digit code to {email}.",
        verifyEmailInstruction: "Enter the code to activate your account.",
        verifyEmailCodeLabel: "Confirmation code",
        verifyEmailCodePh: "6 digits",
        verifyEmailConfirmCta: "Confirm email",
        verifyEmailResendCta: "Send new code",
        verifyEmailResendSent: "New code sent. Check your email.",
        verifyEmailLoginRequired: "Confirm your email to continue.",
        verifyEmailErrorInvalid: "Invalid code. Check it and try again.",
        verifyEmailErrorExpired: "This code has expired. Request a new code.",
        verifyEmailErrorTooMany: "Too many attempts. Request a new code.",
        verifyEmailErrorResendSoon: "Please wait a few seconds before requesting a new code.",
        verifyEmailErrorGeneric: "Could not confirm. Please try again.",
        verifyEmailSendFailed: "Could not send the code. Please try again.",
        verifyEmailSendFailedRetry: "Tap \"Send new code\" to try again.",
        // Phase 12 — password recovery (EN)
        forgotPasswordTitle: "Reset your password",
        forgotPasswordHelper: "Enter your email and we'll send you a 6-digit code.",
        forgotPasswordCta: "Send code",
        forgotPasswordSent: "If this email exists, a password recovery code has been sent.",
        forgotPasswordBackToLogin: "Back to sign in",
        resetPasswordTitle: "Set a new password",
        resetPasswordHelper: "Enter the code we sent to {email} and your new password.",
        resetPasswordCodeLabel: "6-digit code",
        resetPasswordCodePh: "6 digits",
        resetPasswordNewLabel: "New password",
        resetPasswordNewPh: "At least 8 characters",
        resetPasswordCta: "Update password",
        resetPasswordSuccess: "Your password has been updated. You can sign in.",
        resetPasswordSignIn: "Sign in",
        resetPasswordResendCta: "Send new code",
        resetPasswordErrorInvalid: "Invalid code. Check it and try again.",
        resetPasswordErrorExpired: "This code has expired. Request a new code.",
        resetPasswordErrorTooMany: "Too many attempts. Request a new code.",
        resetPasswordErrorResendSoon: "Please wait a few seconds before requesting a new code.",
        resetPasswordErrorWeak: "Password must be at least 8 characters.",
        resetPasswordErrorGeneric: "Could not update. Please try again.",
        biz: {
            creative: "Creative studio",
            cafe: "Café / Restaurant",
            shop: "Shop / Retail",
            services: "Professional services",
            tech: "Tech / Digital",
            other: "Other",
        },
    },
    legal: {
        backToRegister: "Back to sign up",
        termsTitle: "Terms of Service",
        termsLastUpdated: "Last updated: June 2026",
        termsIntro: "Welcome to Ordly. By using the app, you agree to these terms. This is an MVP version — definitive terms will be published before we leave beta.",
        termsAccountTitle: "1. Your account",
        termsAccountBody: "You are responsible for keeping your credentials safe and for all activity under your account.",
        termsUseTitle: "2. Use of the service",
        termsUseBody: "Ordly is designed to organise tax documents (invoices, receipts, etc.). We do not make accounting or tax decisions on your behalf.",
        termsDataTitle: "3. Your data",
        termsDataBody: "Your documents are yours. We store them encrypted and never sell them. See the Privacy Policy for details.",
        termsLiabilityTitle: "4. Limitation of liability",
        termsLiabilityBody: "Ordly is provided \"as is\". We work hard to keep it available and accurate, but we are not liable for accounting decisions taken based on the organised data.",
        termsChangesTitle: "5. Changes",
        termsChangesBody: "We may update these terms. For material changes, you will be notified by email.",
        termsContact: "Questions? Contact support@ordly.com",
        privacyTitle: "Privacy Policy",
        privacyLastUpdated: "Last updated: June 2026",
        privacyIntro: "This policy describes, in plain language, what data we collect, why, and how you can control it.",
        privacyWhatTitle: "1. What we collect",
        privacyWhatBody: "Company name, NIF (tax ID), email, preferred language, and the documents you upload (invoices, receipts, photos).",
        privacyWhyTitle: "2. Why we use it",
        privacyWhyBody: "To organise your documents, remind you of deadlines, and prepare hand-off packages for your accountant. We never use your documents to train public AI models.",
        privacyShareTitle: "3. Sharing",
        privacyShareBody: "We do not sell your data. We only share with vendors strictly required to operate the service (hosting, database).",
        privacyRightsTitle: "4. Your rights",
        privacyRightsBody: "You can export or delete your data at any time from Settings, or by contacting us by email.",
        privacyContact: "Questions? Contact privacy@ordly.com",
    },
    onboarding: {
        stepN: "Step {n} of {total}",
        reassure: "Takes less than 3 minutes.",
        s1Title: "Set up your company",
        s1Sub: "These details help us prepare your workspace.",
        s1Detail: "Company name · NIF · Business type · Language — already filled from sign-up.",
        languageLabel: "Preferred language",
        s2Title: "Invite your team",
        s2Sub: "You can add users later.",
        s3Title: "Add your accountant",
        s3Sub: "You can connect later, when ready.",
        s4TitleL1: "Add Ordly to your",
        s4TitleL2: "Home Screen",
        s4Title: "Add Ordly to your Home Screen",
        s4Sub: "Access Ordly in one tap to capture receipts, upload documents, and stay on top of your business.",
        pwaStep1Title: "Tap the Share icon",
        pwaStep1Desc: "Tap the Share button in your browser.",
        pwaStep2Title: "Choose Add to Home Screen",
        pwaStep2Desc: "Scroll down and select “Add to Home Screen”.",
        pwaStep3Title: "Open Ordly like an app",
        pwaStep3Desc: "Find Ordly on your Home Screen and open it anytime.",
        pwaInfoTitle: "Quick access. All set.",
        pwaInfoDesc: "Open Ordly from your Home Screen to quickly capture receipts and get work done.",
        pwaStep1: "Tap Share",
        pwaStep2: "Choose Add to Home Screen",
        pwaStep3: "Open Ordly like an app",
        finishedTitle: "Upload your first document",
        finishedSub: "Ordly will start preparing your month right away.",
        takeFirstPhoto: "Take first photo",
        uploadDocument: "Upload document",
    },
    capture: {
        title: "Capture expense",
        chooseSource: "Choose how to add",
        takePhoto: "Take a photo",
        takePhotoHint: "Use your camera — invoice, receipt, or QR.",
        pickFromGallery: "Pick from gallery",
        pickFromGalleryHint: "Images, HEIC, or PDFs.",
        dropOrClick: "Drop a file here or tap to choose",
        accepted: "JPG · PNG · HEIC · WebP · PDF (up to 20 MB)",
        thanksToast: "Thank you — document added to the inbox.",
        batchTitle: "Multiple documents",
        batchHint: "Up to {max} in one session",
        sourceEmailTitle: "Receive by email",
        sourceEmailHint: "Forward invoices to your company",
        batchReviewTitle: "Review before uploading",
        batchDoneTitle: "All uploaded",
        batchCount: "{n} of {max} documents",
        batchAddMore: "Add",
        batchAddCamera: "Take another photo",
        batchUploadCta: "Upload {n} documents",
        batchUploadingCta: "Uploading… {done}/{total}",
        batchSeeInbox: "Go to inbox",
        batchRetryFailed: "Retry failed ({n})",
        batchCancel: "Cancel",
        batchRemove: "Remove",
        batchErrorHint: "Failed — please try again.",
        batchDoneSummary: "{ok} ready · {err} failed",
        batchStatus: {
            queued: "Waiting",
            optimizing: "Optimizing",
            uploading: "Uploading",
            processing: "Processing",
            done: "Ready",
            error: "Error",
        },
        optimizing: "Optimizing image…",
        uploading: "Uploading… {pct}%",
        processing: "Reading invoice data…",
        step1Preparing: "Preparing image…",
        step2QR: "Reading fiscal QR…",
        step3Invoice: "Reading invoice data…",
        step4Saving: "Saving document…",
        processingHelper: "This may take a few seconds.",
        done: "Document ready",
        savedTitle: "Saved!",
        savedHelper: "Carry on. We'll finish reading for you.",
        readingInBackground: "Finishing analysis…",
        // Phase A regression fix — restore the staged progress beat.
        // Five stages with % so progress feels measurable.
        bgStep1: "Document saved",
        bgStep2: "Reading fiscal QR…",
        bgStep3: "Reading invoice data…",
        bgStep4: "Finishing analysis…",
        bgStep5: "Details ready to review",
        pollTimeoutSavedKeepReading: "Document saved · reading may continue in the inbox.",
        failed: "Processing failed",
        retry: "Try again",
        addAnother: "Add another",
        goToInbox: "View in inbox",
        sizeBefore: "Before: {size}",
        sizeAfter: "After: {size}",
        ocrDemoBadge: "OCR DEMO",
        ocrRealBadge: "Values extracted by OCR",
        qrBadge: "Fiscal QR read",
        qrPromptTitle: "Add a QR close-up?",
        qrPromptHelper: "A close-up of the QR helps read the official invoice data.",
        qrPromptAddCta: "Add QR",
        qrPromptSkipCta: "Continue without QR",
        qrPromptCloseupHint: "Take a photo of the fiscal QR only. Keep the paper flat and move closer to the QR.",
        possibleDuplicate: "Possible duplicate",
        cameraSource: "Camera",
        uploadSource: "Upload",
        cancel: "Cancel",
        close: "Close",
        // Phase 16 Stage 4 — extracted-fields card labels for the success
        // state.
        metaTotal: "Total",
        metaNumber: "No.",
        metaDate: "Date",
        metaCategory: "Category",
        // ------- Phase Pay (Jul 2026) — payment method prompt -------
        paymentTitle: "How was it paid?",
        paymentHelper: "Optional — you can add or change this later in the document detail.",
        paymentSkip: "Add later",
        paymentSaving: "Saving…",
        paymentSaved: "Saved",
        paymentFailed: "Couldn't save. Please try again.",
        paymentNotSpecified: "Not specified",
        saveAndAddAnother: "Add another",
        viewDocument: "View document",
        paymentMethods: {
            company_card: "Company card",
            company_bank_account: "Company bank account",
            cash: "Cash",
            personal_method: "Personal payment method",
            unpaid: "Not paid yet",
        },
        // Phase 17 (Jul 2026) — Document context prompt.
        contextTitle: "Add context",
        contextHelper: "Optional — this will be shared with your accountant.",
        contextEmployeeHelper: "Optional — this will be shared with the company and accountant.",
        contextPlaceholder: "E.g. client meal, business trip, office supplies…",
        contextSave: "Save context",
        contextSaved: "Saved",
        contextFailed: "Couldn't save. Please try again.",
    },
    inbox: {
        title: "Inbox",
        empty: "No documents yet",
        emptyHint: "Take a photo of your first invoice to get started.",
        startCapture: "Take first photo",
        addManually: "Upload document",
        sectionRecent: "Recent",
        statusReceived: "Received",
        statusReading: "Reading",
        statusReady: "Ready",
        statusNeedsContext: "Needs context",
        statusNeedsNewPhoto: "Retake photo",
        statusPossibleDuplicate: "Possible duplicate",
        statusFailed: "Error",
        statusPersonal: "Personal",
        statusMissingReceipt: "No receipt",
        filterAll: "All",
        filterPending: "To review",
        filterReady: "Ready",
        filterPersonal: "Personal",
        filterMissing: "Missing receipt",
        filterSent: "Sent",
        filterEmail: "Email",
        sourceEmailBadge: "Received by email",
        // Phase Pay (Jul 2026) — selective Inbox chips.
        chipPersonalMethod: "Personal means",
        chipUnpaid: "Unpaid",
        monthFilterLabel: "Month",
        monthFilterAll: "All months",
        headingCountOne: "{n} document",
        headingCountMany: "{n} documents",
        sentOn: "Sent on {date}",
        emptySentTitle: "No documents have been sent yet.",
        emptySentBody: "Documents will appear here after they are included in a package for your accountant.",
        monthContext: "Showing documents from {month}",
        viewAllMonths: "View all months",
        searchPlaceholder: "Search supplier or number…",
        pendingTitle: "Needs your attention",
        pendingNone: "All clear for now.",
        noResults: "No results.",
        emptyMonthGeneric: "No documents in {month}",
        emptyMonthPending: "No documents to review in {month}",
        emptyMonthReady: "No ready documents in {month}",
        emptyMonthNeedsPhoto: "No documents need a new photo in {month}",
        emptyMonthPersonal: "No personal documents in {month}",
        emptyMonthMissing: "No no-receipt expenses in {month}",
        emptyMonthHelper: "You can change the filter or view all months.",
        backToInbox: "Back to inbox",
    },
    detail: {
        title: "Expense detail",
        backBottom: "Back",
        extractedFrom: "Extracted data",
        assignedMonth: "Document assigned to {month}",
        dateSourceQrLabel: "Read from fiscal QR",
        dateEditCorrectLabel: "Correct extracted date",
        dateEditAddLabel: "Add invoice date",
        dateEditHint: "This date must match the date on the invoice.",
        previewUnavailable: "Could not preview this document.",
        previewOpenOriginal: "Open original document",
        previewPdfHint: "This document is a PDF. Open it to view.",
        viewerTitle: "Original document",
        viewerLoading: "Loading document…",
        viewerFallbackMsg: "Your device cannot embed the file here. Tap to open it in a new tab.",
        viewerFallbackCta: "Open in new tab",
        viewerOpenCta: "Open original document",
        viewerOpenInSafari: "Open original in Safari",
        reprocessAction: "Reprocess document",
        reprocessHint: "We'll re-read the same file with the current extraction engine.",
        reprocessInProgress: "Reprocessing…",
        reprocessSuccess: "Reprocessing complete.",
        reprocessAlreadyInProgress: "Already reprocessing.",
        reprocessError: "Could not reprocess. Please try again.",
        reprocessFailed: "Reprocessing failed. Please try again or contact support.",
        reprocessTimeout: "Processing is taking longer than expected.",
        reprocessRefresh: "Refresh status",
        previewPdfPageLabel: "Page 1 of {n}",
        sourceQr: "Fiscal QR read",
        sourceOcr: "Please confirm these values",
        sourceOcrReal: "Values extracted by OCR",
        sourceEmail: "Received by email",
        emailFrom: "From",
        emailSubject: "Subject",
        emailReceived: "Received",
        emailFilename: "File",
        qrSourceHint: "Values read directly from the invoice QR.",
        ocrConfirmBadge: "Please confirm the values",
        ocrDemoBadge: "OCR DEMO",
        nifMismatchTitle: "The invoice VAT number does not match the registered company VAT number.",
        nifMismatchHint: "Please confirm this document belongs to the company before including it in the package.",
        nifMismatchToast: "Document saved, but needs confirmation: VAT number mismatch.",
        qrBadge: "QR detected",
        supplier: "Supplier",
        supplierNif: "Supplier NIF",
        amount: "Total",
        date: "Date",
        documentType: "Type",
        invoiceNumber: "Number",
        category: "Category",
        notes: "Notes for your accountant",
        notesPlaceholder: "Anything useful for your accountant…",
        // Phase 17 (Jul 2026) — Editable document context.
        contextSectionTitle: "Document context",
        contextAdd: "Add context",
        contextSave: "Save context",
        contextPlaceholder: "E.g. client meal, business trip, office supplies…",
        contextSaved: "Saved",
        contextFailed: "Couldn't save. Please try again.",
        // Phase Pay (Jul 2026) — Document Detail payment-method strip
        paymentSectionTitle: "Payment method",
        paymentSectionEmpty: "Not specified",
        paymentSectionRemove: "Remove",
        editFields: "Suggest correction",
        cancel: "Cancel",
        save: "Save",
        actionsTitle: "Actions",
        markPersonal: "Mark as personal",
        unmarkPersonal: "Unmark personal",
        excludeFromPackage: "Exclude from package",
        includeInPackage: "Include in package again",
        requestRemoval: "Delete document",
        // Stage 3B — send to accountant
        sendToAccountant: "Send to accountant",
        sendingToAccountant: "Sending…",
        sentToAccountant: "Sent to accountant",
        sendIneligible: "This document cannot yet be sent to the accountant.",
        sendNoAccountant: "There is no active accountant connection for this company.",
        removalReasonLabel: "Reason (optional)",
        confirmRemoval: "Delete document",
        removalNote: "The document will be removed from your inbox and will no longer be included in packages.",
        questionsTitle: "Context questions",
        questionsAnswered: "Answered",
        answerYes: "Yes",
        answerNo: "No",
        answerSave: "Save answer",
        duplicatesTitle: "Similar documents",
        duplicatesOpen: "Compare duplicates",
        duplicateConfirm: "Not a duplicate",
        duplicateMerge: "Same document",
        markMissing: "Mark as missing receipt",
        unmarkMissing: "I have the receipt",
        removalRequested: "Removal requested",
    },
    missingReceipt: {
        title: "Expense without receipt",
        subtitle: "Add what you remember. Your accountant sees this was added manually.",
        supplier: "Supplier",
        supplierPh: "e.g. Restaurante O Cantinho",
        date: "Date",
        amount: "Amount (€)",
        amountPh: "e.g. 12.50",
        notes: "Notes (optional)",
        notesPh: "Anything to explain to your accountant…",
        submit: "Add to inbox",
        added: "Added to your inbox",
    },
    dashboard: {
        helloMorning: "Good morning",
        helloAfternoon: "Good afternoon",
        helloEvening: "Good evening",
        installPrompt: {
            title: "Add Ordly to your Home Screen",
            body: "Open Ordly faster and capture documents in one tap.",
            cta: "How to install",
            dismiss: "Not now",
        },
        emptyTitle: "Start your month",
        emptyBody: "Snap a photo of an invoice or receipt. Ordly handles the organization for your accountant.",
        primary: "Take first photo",
        secondary: "Upload document",
        addDocument: "Add document",
        addPhoto: "New photo",
        recentTitle: "Recent",
        seeAll: "See all",
        readinessZero: "0% ready",
        thisMonthLabel: "This month",
        viewInbox: "Inbox",
        inboxPendingLabel: "Pending",
        readinessRingLabel: "Ready for your accountant",
        recentActivityTitle: "Recent activity",
        expectedTitle: "What usually arrives",
        expectedHint: "Tick what you already sent. See what's missing.",
        monthsAllTitle: "Monthly boxes",
        monthDetailBack: "Back",
        statTotal: "Total",
        statReady: "Ready",
        statPending: "Pending",
        statPersonal: "Personal",
        statMissing: "No receipt",
        statNeedsPhoto: "Needs photo",
        statToReview: "To review",
        amountTotal: "Monthly total",
        headlineReady: "{month} is ready for your accountant",
        headlinePartial: "{month} is {pct}% ready for your accountant",
        headlineEmpty: "{month} has no documents yet",
        readinessHelper: "Based on the documents added so far.",
        readinessHelperEmpty: "Add documents to start preparing the month.",
        monthSelectorAria: "Change month",
        estimatedSummaryTitle: "Estimated summary",
        estimatedTotalLabel: "Estimated monthly total",
        visibleVatLabel: "Visible VAT",
        subjectToReview: "Subject to accountant review",
        docsSavedTitle: "Documents saved",
        actionsTitle: "Actions",
        actionTakePhoto: "Take a photo",
        actionTakePhotoHelper: "Snap a receipt",
        actionUpload: "Upload document",
        actionUploadHelper: "From your device",
        actionScanMultiple: "Scan multiple",
        actionScanMultipleHelper: "Several receipts at once",
        actionPreparePackage: "Prepare package",
        actionPreparePackageHelper: "Review and send",
        needsAttentionTitleOne: "1 document needs your attention",
        needsAttentionTitleMany: "{n} documents need your attention",
        needsAttentionBody: "{supplier} {reason} before it can go to your accountant.",
        needsAttentionBodyNoSupplier: "Some documents {reason} before they can go to your accountant.",
        needsAttentionReasonContext: "needs context",
        needsAttentionReasonPhoto: "needs a better photo",
        needsAttentionReasonDuplicate: "may be a duplicate",
        needsAttentionReasonContextPlural: "need context",
        needsAttentionReasonPhotoPlural: "need a better photo",
        needsAttentionReasonDuplicatePlural: "may be duplicates",
        needsAttentionCta: "Resolve now",
        needsAttentionCrossTitleOne: "1 document from another month needs your attention",
        needsAttentionCrossTitleMany: "{n} documents from other months need your attention",
        needsAttentionCrossBody: "{supplier} belongs to {month} and {reason}.",
        needsAttentionCrossBodyNoSupplier: "{n} documents belong to earlier months and {reason}.",
        crossMonthUploadTitle: "Invoice saved in {month}",
        crossMonthUploadBody: "The invoice date points to {monthShort}, so we saved it in that month.",
        crossMonthUploadCtaView: "View {monthShort}",
        crossMonthUploadCtaOk: "OK",
        batchCaptureTitle: "Add several",
        batchCaptureHint: "Upload multiple invoices at once.",
        handoffPill: {
            noneTitle: "No accountant connected yet",
            noneCta: "Add accountant",
            notSentTitle: "Not sent this month yet",
            notSentCta: "Send to accountant",
            sentToday: "Package sent today",
            sentYesterday: "Package sent yesterday",
            sentOnDate: "Last package sent on {date}",
            sentRelative: "Last handoff: {month} · sent {n} {unit} ago",
            unitDay: "day",
            unitDays: "days",
            unitHour: "hour",
            unitHours: "hours",
            ariaOpenAccountant: "Open Accountant",
        },
    },
    months: {
        title: "Monthly boxes",
        all: "All",
        nothingYet: "No documents in this month yet.",
        monthOf: "Month of {label}",
        readiness: "Ready",
        gotoCapture: "Capture new",
        markAsExpected: "Mark as expected",
        // Phase Month Detail visual refinement (Feb 2026)
        heroReviewedHeadlineReady: "{month} is reviewed",
        heroReviewedHeadlinePartial: "{month} is {pct}% reviewed",
        heroReviewedHeadlineEmpty: "{month} has no documents yet",
        heroReviewedHelper: "Based on the documents added so far.",
        heroReviewedHelperEmpty: "Add documents to start preparing the month.",
        countersDocs_one: "1 document",
        countersDocs_other: "{n} documents",
        countersToReview_one: "1 to review",
        countersToReview_other: "{n} to review",
        deliveryCardTitle: "Accountant delivery",
        deliveryNoSendYet: "Nothing from this month has been sent to the accountant yet.",
        // Phase 15 — Meses as summary/history only
        notSentYet: "Not sent yet",
        estimatedTotal: "Estimated total",
        visibleVat: "Visible VAT",
        statReady: "Ready",
        statToReview: "To review",
        statMissing: "No receipt",
        reviewPending: "Review pending",
        sendViaAccountant: "Send documents through Accountant",
        monthSummary: "Month summary",
        // Month Handoff Status Refinement (Feb 2026)
        preparation: "Preparation",
        handoff: "Accountant delivery",
        lastSend: "Last send",
        notYetSent: "Not sent yet",
        viewSentDocs_one: "View sent document",
        viewSentDocs_other: "View sent documents",
        docsCount_one: "1 document",
        docsCount_other: "{n} documents",
        docsReviewed_one: "1 document reviewed",
        docsReviewed_other: "{n} documents reviewed",
        docsToReview_one: "1 to review",
        docsToReview_other: "{n} to review",
        docsSent_one: "1 sent",
        docsSent_other: "{n} sent",
        docsUnsent_one: "1 to send",
        docsUnsent_other: "{n} to send",
        newDocs_one: "1 new document",
        newDocs_other: "{n} new documents",
        newSinceLastSend_one: "1 new document since the last send",
        newSinceLastSend_other: "{n} new documents since the last send",
        readyForNextSend_one: "1 document ready for the next send",
        readyForNextSend_other: "{n} documents ready for the next send",
        readyForFirstSend_one: "1 ready for first send",
        readyForFirstSend_other: "{n} ready for first send",
        needsAttention_one: "1 document needs your attention",
        needsAttention_other: "{n} documents need your attention",
        nothingToSend: "nothing to send",
        organisedMonth: "Month organised",
        percentReviewed: "{pct}% reviewed",
        // Hero labels
        heroPrepLabel: "Reviewed",
        heroPrepLabelLong: "Documents reviewed",
        heroReadyForFirst: "Ready for first send",
        heroReadyNext: "Ready for next send",
        heroOrganised: "Month organised",
        heroAttention: "Needs your attention",
        heroAttentionDocs_one: "1 document to review",
        heroAttentionDocs_other: "{n} documents to review",
        // Per-document delivery badges
        docBadge: {
            sent: "Sent on {date}",
            new: "New",
            readyNext: "Ready for next send",
            toReview: "To review",
        },
        // SendDetail screen
        send: {
            title: "Sent on {date}",
            backToMonth: "Back to month",
            deliveryFailed: "Delivery failed",
            delivered: "Delivered",
            sent: "Sent",
            opened: "Opened",
            downloaded: "Downloaded",
            bounced: "Bounced",
            complained: "Marked as spam",
            queued: "Sent",
            unknownDelivery: "Unknown status",
            empty: "This send does not include any document from this month.",
            loading: "Loading send…",
            notFound: "Send not found.",
        },
    },
    bottomNav: {
        home: "Home",
        inbox: "Inbox",
        capture: "Capture",
        months: "Months",
        accountant: "Accountant",
        settings: "Settings",
    },
    accountantHandoff: {
        eyebrow: "Ordly handoff",
        title: "Accountant",
        subtitle: "Add your accountant and send document packages securely.",
        emptyTitle: "Add accountant",
        emptyHelper: "To send the organized month, add the accountant email.",
        saveHelper: "Add at least the email and a name or firm.",
        reassurance: "You can add or change the accountant later.",
        nameLabel: "Accountant name",
        firmLabel: "Accounting firm",
        emailLabel: "Accountant email",
        phoneLabel: "Phone (optional)",
        handoffRhythm: "Sending preference",
        rhythmMonthly: "Monthly",
        rhythmWeekly: "Weekly",
        rhythmOnRequest: "On request",
        saveAccountant: "Save accountant",
        editConnection: "Edit",
        currentPackage: "Current package",
        packageMonthFmt: "{monthName} package",
        summaryLoadError: "Could not load the package. Please try again.",
        summaryRetryCta: "Try again",
        summaryRetrying: "Retrying...",
        statusReady: "Ready to send",
        statusNeedsReview: "Needs attention",
        statusEmpty: "No documents",
        readyDocuments: "ready documents",
        needContext: "need context",
        possibleDuplicates: "possible duplicates",
        sendPackage: "Send to accountant",
        sending: "Sending…",
        resolveBeforeSending: "Resolve pending documents before sending.",
        emptyPeriod: "There are no documents in this period yet.",
        lastHandoff: "Last handoff",
        lastHandoffFmt: "{period} package sent to {email} on {date}",
        // Phase Month-Send-Unsent (Feb 2026) — Mais opções + new states
        alreadySent: "already sent",
        alreadySentOne: "already sent",
        unsentDocs: "documents to send",
        unsentDocsOne: "document to send",
        everythingSent: "Everything sent",
        viewLastSend: "View last send",
        moreOptions: "More options",
        sendFullMonth: "Send full month",
        viewPreviousSends: "View previous sends",
        confirmFullMonthTitle: "Send the full month?",
        confirmFullMonthBody: "This package will include {n} documents:",
        confirmFullMonthAlreadySent: "{n} previously sent",
        confirmFullMonthAlreadySentPlural: "{n} previously sent",
        confirmFullMonthNew: "{n} not yet sent",
        confirmFullMonthNewPlural: "{n} not yet sent",
        confirmFullMonthFooter: "A new package and a new secure link will be created.",
        confirmFullMonthCta: "Send full month",
        sendErrorNoUnsent: "There are no new documents to send in this month.",
        previousSendsTitle: "Previous sends for {monthName}",
        previousSendsEmpty: "No previous sends for this month yet.",
        previousSendsClose: "Close",
        previousSendOneDoc: "{n} document",
        previousSendDocsPlural: "{n} documents",
        viewPackage: "View package",
        resendPackage: "Resend",
        confirmResendTitle: "Resend this package?",
        confirmResendBody: "It will be sent again to:",
        confirmResendCta: "Resend package",
        confirmResendCancel: "Cancel",
        resendHistoricalSuccess: "Package resent.",
        // Phase 12c — Resend webhook delivery pill
        delivery: {
            queued: "Email queued",
            delivered: "Email delivered",
            bounced: "Email bounced",
            complained: "Complaint received",
            failed: "Email failed",
            unknown: "Status unknown",
        },
        deliveryWarning: "Check the accountant email or send again.",
        // Phase 12d — manual resend action
        complaintHelper: "The recipient marked this email as spam. Contact them before sending again.",
        resendHelper: "Check the accountant email before resending.",
        resendCta: "Resend email",
        resendSending: "Resending…",
        resendSuccess: "Email resent.",
        resendFailed: "Could not resend. Please try again.",
        resendCooldown: "Wait {seconds}s before resending.",
        resendNotApplicable: "Resend is not available from the current state.",
        securityNote: "Only documents included in the sent package are available through the secure link.",
        confirmTitle: "Send {monthName} package?",
        confirmBody: "Ordly will send a secure link to {email} with the ready documents for this period.",
        confirmCta: "Send package",
        confirmCancel: "Cancel",
        sentToastTitle: "Package sent",
        sentToastBody: "Secure link sent to {email}.",
        sendErrorTitle: "Could not send the package. Please try again.",
        sendErrorBody: "Check your connection and try again. If the problem persists, contact support.",
        months: {
            "01": "January", "02": "February", "03": "March", "04": "April",
            "05": "May",     "06": "June",     "07": "July",  "08": "August",
            "09": "September","10": "October","11": "November","12": "December",
        },
    },

    activityFeed: {
        document_uploaded: "Invoice added",
        document_processed: "Document processed",
        document_processing_started: "Processing document",
        document_processing_failed: "Processing failed",
        document_edited: "Document updated",
        document_action_mark_personal: "Marked as personal",
        document_action_unmark_personal: "Unmarked personal",
        document_action_toggle_missing_receipt: "No-receipt expense updated",
        document_action_include_in_package: "Added to package",
        document_action_exclude_from_package: "Removed from package",
        document_action_answer_question: "Document needs context",
        document_removal_requested: "Document removed",
        document_retry_requested: "Retry requested",
        missing_receipt_created: "No-receipt expense added",
        package_created: "Package prepared",
        package_generated: "Package generated",
        package_sent: "Package sent to accountant",
        package_opened: "Accountant opened the package",
        package_downloaded: "Accountant downloaded the package",
        package_revoked: "Package revoked",
        package_regenerated: "Package regenerated",
        fallback: "Activity",
    },
    packages: {
        title: "Accountant package",
        listTitle: "Sent packages",
        empty: "You haven't sent any packages yet.",
        createForMonth: "Prepare monthly package",
        createForThisMonth: "Prepare this month's package",
        backToMonth: "Back to month",
        backToPackages: "Back to packages",
        loading: "Preparing package...",
        generating: "Generating files...",
        sending: "Sending...",
        revoking: "Revoking...",
        regenerating: "Regenerating...",
        steps: {
            review: "Review",
            generate: "Generate",
            send: "Share",
        },
        state: {
            draft: "Draft",
            ready: "Ready to send",
            sent: "Sent",
            opened: "Opened by accountant",
            downloaded: "Downloaded",
            expired: "Expired",
            revoked: "Revoked",
            superseded: "Superseded",
        },
        review: {
            heading: "Review the package",
            documents: "{n} document included",
            documentsPlural: "{n} documents included",
            amountTotal: "Monthly total (excl. personal)",
            included: "Included",
            personal: "Personal",
            missing: "No receipt",
            empty: "No documents are ready for this month yet.",
        },
        generate: {
            heading: "Generate package files",
            description: "We'll bundle the images, metadata.csv, context_answers.csv and summary.txt into one ZIP.",
            cta: "Generate package",
            zipSize: "ZIP {size}",
        },
        send: {
            heading: "Share with the accountant",
            description: "We create a secure link valid for 14 days. The accountant doesn't need to sign up.",
            accountantName: "Accountant name",
            accountantNamePh: "e.g. Maria Silva",
            accountantEmail: "Accountant email",
            accountantEmailPh: "maria@accounting.example",
            note: "Note (optional)",
            notePh: "Anything they should know about this month?",
            cta: "Generate link & send",
            ctaResend: "Generate new link",
            successTitle: "Link ready",
            successHint: "Copy and send to the accountant — email, WhatsApp, however you prefer.",
            copy: "Copy link",
            copied: "Copied!",
            expiresOn: "Expires on {date}",
        },
        share: {
            link: "Share link",
            tokenSuffix: "ends in …{suffix}",
            revokedHint: "This link has been revoked.",
            expiredHint: "This link has expired.",
            supersededHint: "Superseded by a newer package.",
        },
        actions: {
            revoke: "Revoke link",
            revokeConfirm: "Are you sure? The accountant will lose access.",
            regenerate: "Regenerate package",
            regenerateConfirm: "This creates a new package. The current one becomes superseded.",
            download: "Download (me)",
        },
        events: {
            title: "Activity",
            empty: "No events yet.",
            created: "Package created",
            generated: "Files generated",
            sent: "Link sent",
            opened: "Opened by accountant",
            downloaded: "Downloaded by accountant",
            revoked: "Link revoked",
            regenerated: "Package regenerated",
            expired: "Link expired",
        },
        meta: {
            sentAt: "Sent",
            openedAt: "Opened",
            downloadedAt: "Downloaded",
            opens: "Opens",
            downloads: "Downloads",
            expiresIn: "Expires in {days} days",
            expiresToday: "Expires today",
            expiredOn: "Expired on {date}",
        },
    },
    accountant: {
        title: "Accountant package — {month} {year}",
        from: "Company",
        sentBy: "{company} shared this package with you using Ordly.",
        sentAt: "Sent on {date}",
        sentAtLabel: "Sent on",
        validUntilLabel: "Link valid until",
        referenceLabel: "Reference",
        expiresIn: "Expires in {days} days",
        contents: "Contents",
        documents: "{n} document",
        documentsPlural: "{n} documents",
        amountTotal: "Total (excl. personal)",
        download: "Download package (.zip)",
        downloading: "Downloading...",
        downloaded: "Downloaded",
        notFound: "This package is not available.",
        revoked: "This link has been revoked by the company.",
        expired: "This link has expired. Please ask the company for a new one.",
        notReady: "This package is not ready yet.",
        zipContents: {
            heading: "The package includes:",
            summary: "Monthly summary as PDF",
            metadata: "Document data as CSV",
            context: "Context answers as CSV",
            documents: "{n} original document",
            documentsPlural: "{n} original documents",
        },
        attribution: "Prepared with Ordly",
        learnMore: "Learn more about Ordly",
        notePrefix: "Note from the company:",
        secureBadge: "Secure and private link",
        secureValidUntil: "Valid until {date}",
        secureLineExpired: "Link expired",
        amountDash: "—",
        docPreview: {
            open: "Open document",
            openInNewTab: "Open in new tab",
            close: "Close",
            cannotRender: "Your browser could not display this document.",
            loading: "Loading preview...",
        },
    },
    settings: {
        title: "Settings",
        subtitle: "Your account and Ordly workspace",
        role: {
            owner: "Owner",
            manager: "Manager",
            captureOnly: "Capture only",
            member: "Member",
        },
        language: {
            title: "Language",
            hint: "The whole app — including the accountant package — uses this language.",
        },
        pwa: {
            title: "Add to Home Screen",
            hint: "Open Ordly faster, just like an app.",
            installCta: "How to install",
        },
        installGuide: {
            title: "Add Ordly to your Home Screen",
            intro: "Open Ordly faster and capture documents in one tap.",
            iphone: "iPhone",
            android: "Android",
            ios: {
                step1Title: "Tap Share",
                step1Body: "In Safari, tap the Share button.",
                step2Title: "Choose \u201cAdd to Home Screen\u201d",
                step2Body: "Scroll down and select \u201cAdd to Home Screen\u201d.",
                step3Title: "Tap \u201cAdd\u201d",
                step3Body: "Ordly will appear on your iPhone Home Screen.",
            },
            androidSteps: {
                step1Title: "Open the browser menu",
                step1Body: "Tap the browser menu in the top corner.",
                step2Title: "Choose \u201cAdd to Home Screen\u201d",
                step2Body: "On some devices this may appear as \u201cInstall app\u201d.",
                step3Title: "Confirm installation",
                step3Body: "Ordly will appear on your phone Home Screen.",
            },
            done: "Done",
            back: "Back",
        },
        comingSoon: {
            title: "Coming soon",
            team: "Invite teammates",
            permissions: "Manage permissions",
            invite: "Invite your accountant",
            notifications: "Notifications",
            tag: "Coming soon",
        },
        signOut: "Sign out",
        signOutHelper: "Sign out of this account on this device.",
        backHome: "Back to This month",
        demoTag: "DEMO",
        company: {
            title: "Company",
            edit: "Edit",
            save: "Save",
            cancel: "Cancel",
            name: "Name",
            nif: "NIF",
            nifLocked: "Locked",
            nifLockHint: "NIF is fixed after registration to keep the document history consistent.",
            businessType: "Business type",
            businessTypePh: "e.g. Design Studio",
            currency: "Currency",
            savedToast: "Company details updated.",
            errorToast: "Couldn't save — please check the fields.",
        },
        frequency: {
            title: "Package frequency",
            hint: "How often you share packages with your accountant",
            opts: {
                monthly: "Monthly",
                biweekly: "Biweekly",
                manual: "Manual only",
            },
            optDesc: {
                monthly: "Reminder at the start of each month",
                biweekly: "Reminder every 14 days",
                manual: "No automatic reminders",
            },
        },
        reminders: {
            title: "Reminders",
            hint: "Reminders are simulated — no real email is sent yet.",
            nextAt: "Next reminder: {date}",
            manualOnly: "No automatic reminders scheduled.",
            simulateCta: "Simulate reminder (DEMO)",
            simulatedToast: "Reminder simulated — nothing was sent.",
            savedToast: "Frequency updated.",
        },
        inbox: {
            title: "Forward to your Ordly",
            hint: "Forward invoices to this address — when we wire up the real intake, they'll appear in your inbox automatically. Mocked for now.",
            copy: "Copy",
            copiedToast: "Address copied.",
            simulateCta: "Simulate forwarding (DEMO)",
            simulatedToast: "Forwarding simulated — nothing was processed.",
        },
    },
    demo: {
        label: "DEMO DATA",
        emailSimulated: "Simulated email in demo environment",
        ocrMock: "OCR DEMO",
        valuesEstimated: "Estimated values",
    },
    emailIntake: {
        title: "Receive invoices by email",
        helper: "Forward your invoices to:",
        loadingAddress: "Loading address…",
        copy: "Copy address",
        share: "Share",
        copied: "Address copied",
        copyFailed: "Couldn't copy. Select and copy manually.",
        processingHint: "Supported attachments will be added automatically to your Inbox.",
        howTitle: "How it works",
        howStep1: "Forward the email containing the invoice",
        howStep2: "Ordly processes the attachments",
        howStep3: "Review the details in your Inbox",
        supportedTitle: "Supported attachments",
        supportedBody: "PDF, JPG, PNG, HEIC or WebP · up to 20 MB per file",
        supportedPasswordNote: "Password-protected PDFs cannot be processed.",
        noSupportedAttachmentDetail: "We did not find a supported attachment in this email.",
        recentTitle: "Recent activity",
        recentEmpty: "No invoices have been received by email yet.",
        viewAllInbox: "View all in Inbox",
        aliasMissing: "Email intake is not yet enabled for this company.",
        unavailable: "This feature is not yet available for this company.",
        unavailableDetail: "When it becomes active, you will be able to forward invoices to a dedicated Ordly address.",
        backToCapture: "Back to Capture",
        attachmentsOne: "1 attachment",
        attachmentsMany: "{n} attachments",
        attachmentsZero: "No attachments",
        senderUnknown: "Unknown sender",
        eventState: {
            processing: "Processing attachments",
            processed: "Processed",
            partially_processed: "Partially processed",
            no_supported_attachments: "No supported invoices",
        },
    },
    employee: {
        greeting: "Hello, {name}",
        myExpenses: "My expenses",
        monthLabel: "{month}",
        documentsLabel: "{count} documents",
        identifiedAmount: "{amount} identified",
        personallyPaid: "{amount} paid personally",
        needsAttention: "{count} needs your attention",
        needsAttentionPlural: "{count} need your attention",
        captureCta: "Take a photo",
        uploadCta: "Upload document",
        recentDocuments: "Recent documents",
        empty: "You have not submitted any documents yet.",
        incompleteNote: "Based on amounts identified so far.",
        nav: {
            home: "Home",
            capture: "Capture",
            documents: "My documents",
        },
        status: {
            saved: "Saved",
            processing: "Processing",
            ready: "Ready",
            needs_attention: "Needs your attention",
            possible_duplicate: "Possible duplicate",
            awaiting_owner_review: "Waiting for company review",
            included_in_next_send: "Included in the next send",
            shared_with_accountant: "Shared with the accountant",
        },
        detail: {
            title: "Document",
            note: "Note",
            notePlaceholder: "Quick detail for the company owner.",
            paymentMethod: "Payment method",
            amount: "Amount",
            date: "Document date",
            supplier: "Supplier",
            save: "Save",
            saved: "Saved.",
            saveError: "Couldn't save.",
            retry: "Retry",
            remove: "Delete",
            confirmRemove: "Delete this document?",
            packageLocked:
                "Already shared with the accountant. Talk to the company owner for any correction.",
            paymentMethods: {
                company_card: "Company card",
                company_bank_account: "Company bank account",
                cash: "Cash",
                personal_method: "Personal method",
                unpaid: "Unpaid",
                unset: "Not set",
            },
        },
        capture: {
            title: "New document",
            takePhoto: "Take a photo",
            chooseFile: "Choose a file",
            note: "Note (optional)",
            notePlaceholder: "e.g. lunch with client",
            send: "Send",
            sending: "Sending…",
            success: "Document sent.",
            error: "Couldn't send the document.",
            tooLarge: "File too large (max 20MB).",
            paymentMethodLabel: "Payment method",
            paymentMethodHelp: "Optional — you can confirm later.",
            attached: "Attached",
            replace: "Replace",
            optimizing: "Optimising image…",
            uploading: "Uploading document…",
            processing: "Reading document…",
            processingHelp: "This can take a few seconds.",
            timedOut: "This is taking longer than expected. You can keep waiting or try again.",
            qrPromptTitle: "QR code not detected automatically",
            qrPromptBody: "If the document has a QR code, you can take a closer photo to help Ordly read the information.",
            qrPromptTakeQr: "Take QR photo",
            qrPromptContinue: "Continue without QR",
            qrPromptRetake: "Retake document photo",
            qrAssistAttached: "QR photo attached.",
            confirmTitle: "Confirm the information",
            confirmHelp: "Check and complete anything that's missing.",
            confirmFieldSupplier: "Supplier",
            confirmFieldDate: "Document date",
            confirmFieldAmount: "Total",
            confirmFieldPayment: "Paid with",
            confirmFieldNote: "Note",
            // Phase 17 (Jul 2026) — Employee post-capture context.
            confirmFieldContext: "Add context",
            confirmFieldContextHelper: "Optional — this will be shared with the company and accountant.",
            confirmFieldContextPlaceholder: "E.g. client meal, business trip, office supplies…",
            confirmFieldContextSave: "Save context",
            confirmFieldContextSaved: "Context saved",
            confirmFieldContextError: "We couldn't save the context. Please try again.",
            confirmMissing: "Missing",
            confirmLocked: "Read from QR code",
            confirmCta: "Confirm and send",
            confirmSaving: "Saving…",
            confirmSaveError: "Couldn't save the changes.",
            duplicateTitle: "This document may already have been submitted.",
            duplicateBody: "The company makes the final call. You can still submit it if you wish.",
            duplicateConfirm: "Submit anyway",
            unreadableTitle: "We could not read this document.",
            unreadableBody: "Take another photo showing the full document, with good lighting and no reflections.",
            unreadableRetake: "Retake photo",
            retry: "Retry",
        },
        revoked: {
            title: "This access is no longer active.",
            body: "Ask the company owner for a new link.",
        },
        loading: "Loading…",
        homeScreen: {
            title: "Add to home screen",
            iosStep1: "Tap Share",
            iosStep2: "Choose “Add to Home Screen”",
            iosStep3: "Tap Add",
            androidBody:
                "Open the browser menu and choose “Add to Home screen”.",
            dismiss: "Later",
            cta: "How to add",
        },
    },
    ownerEmployees: {
        // — Settings entry point —
        settingsRow: "Invite your team",
        // — Page —
        title: "Team",
        add: "Add team member",
        empty: "No team members yet",
        emptyHint: "Invite someone to submit and follow their own company documents.",
        docsLine: "{count} documents · {amount} identified",
        personalLine: "{amount} paid personally",
        needsLine: "{count} need your attention",
        status: {
            active: "Active",
            revoked: "Revoked",
        },
        create: {
            title: "Add team member",
            nameLabel: "Team member name",
            nameHelper: "They’ll receive a private link to upload and follow their documents.",
            namePlaceholder: "Mary Smith",
            submit: "Create access",
            cancel: "Cancel",
            error: "Couldn’t create access. Please try again.",
        },
        reveal: {
            title: "Access created",
            body: "Share this private access with {name}.",
            warning: "The link is shown only once.",
            copy: "Copy link",
            copied: "Link copied",
            whatsapp: "WhatsApp",
            showQr: "Show QR",
            hideQr: "Hide QR",
            qrCaption: "Personal QR for {name}. Don't share it with anyone else.",
            done: "Done",
            close: "Close",
            whatsappBody:
                "Hi {name}. This is your Ordly access to send and follow your company documents:\n\n{link}\n\nOpen the link on your phone and add it to your home screen for quicker access.",
        },
        detail: {
            nameLabel: "Name",
            editName: "Edit",
            saveName: "Save",
            cancelEdit: "Cancel",
            replaceLink: "Replace link",
            revoke: "Remove from team",
            replaceConfirmTitle: "Replace access link?",
            replaceConfirmBody:
                "The previous link and any active sessions will stop working immediately.",
            replaceConfirm: "Replace",
            revokeConfirmTitle: "Remove {name} from the team?",
            revokeConfirmBody:
                "{name} will lose access to Ordly and will be removed from the Team list.\n\nPreviously submitted documents and their history will remain available.",
            revokeConfirm: "Remove from team",
            docsTitle: "Submitted documents ({count})",
            empty: "No documents sent yet.",
            documentFallback: "Document",
        },
    },
    ownerReview: {
        attribution: "Submitted by {name}",
        attributionUnknown: "Submitted by team member",
        awaitingReview: "Awaiting company review",
        includedInNextSend: "Included in next send",
        sharedWithAccountant: "Already shared with accountant",
        includeCta: "Include in next send",
        includeSuccess: "Included in next send.",
        includeError: "Couldn't include.",
    },
    companyBrand: {
        poweredByOrdly: "Powered by Ordly",
        relationship: {
            managedBy: "Managed by your accountant",
            bannerAria: "View accountant details",
            accountantBlock: "Your accountant is {firm}. Documents you share are handled by the {firm} team.",
        },
    },

};

const translations = { "pt-PT": ptPT, en };
const I18nCtx = createContext(null);

export function I18nProvider({ children, initial = "pt-PT" }) {
    const [lang, setLangState] = useState(() => {
        if (typeof window !== "undefined") {
            const saved = localStorage.getItem("ordly_lang");
            if (saved === "pt-PT" || saved === "en") return saved;
        }
        return initial;
    });

    const setLang = useCallback((l) => {
        if (l !== "pt-PT" && l !== "en") return;
        setLangState(l);
        try {
            localStorage.setItem("ordly_lang", l);
        } catch {
            // ignore localStorage failures
        }
    }, []);

    // Phase 7 Batch E — adopt backend-declared language on first login.
    // The auth provider dispatches `ordly:lang-from-backend` after /api/me
    // resolves. We only adopt the value when the user has NOT made an
    // explicit choice yet (i.e. localStorage 'ordly_lang' is unset), so a
    // manual LanguageSwitch toggle always wins on subsequent sessions.
    useEffect(() => {
        function onBackendLang(e) {
            const backendLang = e?.detail?.language;
            if (backendLang !== "pt-PT" && backendLang !== "en") return;
            try {
                if (localStorage.getItem("ordly_lang")) return;
            } catch {
                // localStorage unavailable — fall through and apply anyway
            }
            setLang(backendLang);
        }
        window.addEventListener("ordly:lang-from-backend", onBackendLang);
        return () => window.removeEventListener("ordly:lang-from-backend", onBackendLang);
    }, [setLang]);

    const t = useCallback(
        (path, vars) => {
            const parts = path.split(".");
            let cur = translations[lang];
            for (const p of parts) {
                if (cur == null) break;
                cur = cur[p];
            }
            if (typeof cur !== "string") {
                // Fallback to PT-PT
                let f = translations["pt-PT"];
                for (const p of parts) {
                    if (f == null) break;
                    f = f[p];
                }
                cur = typeof f === "string" ? f : path;
            }
            if (vars) {
                Object.entries(vars).forEach(([k, v]) => {
                    cur = cur.replace(new RegExp(`{${k}}`, "g"), String(v));
                });
            }
            return cur;
        },
        [lang]
    );

    // Plural helper — picks `${basePath}_one` when |n| == 1 and
    // `${basePath}_other` otherwise. Always interpolates `{n}`. PT-PT
    // and EN both follow the simple two-form rule for the labels used
    // by this app, so we keep this deliberately tiny rather than
    // pulling in a full ICU runtime.
    const tn = useCallback(
        (basePath, n, vars) => {
            const key = `${basePath}_${Math.abs(n) === 1 ? "one" : "other"}`;
            return t(key, { n, ...(vars || {}) });
        },
        [t]
    );

    // Locale-aware formatters
    const formatters = useMemo(() => {
        const locale = lang === "pt-PT" ? "pt-PT" : "en-GB"; // EN-GB keeps DD/MM/YYYY per brand kit
        return {
            currency: (cents) =>
                new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format((cents ?? 0) / 100),
            // Render an em-dash when the amount is unknown OR exactly zero (avoids
            // implying a confirmed €0,00 total when nothing has actually been priced).
            currencyOrDash: (cents) =>
                (cents === null || cents === undefined || cents === 0)
                    ? "—"
                    : new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(cents / 100),
            number: (n) => new Intl.NumberFormat(locale).format(n ?? 0),
            date: (d) =>
                d
                    ? new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" }).format(
                          d instanceof Date ? d : new Date(d)
                      )
                    : "—",
            monthLong: (d) =>
                d
                    ? new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(
                          d instanceof Date ? d : new Date(d)
                      )
                    : "—",
            greetingTime: () => {
                const h = new Date().getHours();
                if (h < 12) return lang === "pt-PT" ? "Bom dia" : "Good morning";
                if (h < 19) return lang === "pt-PT" ? "Boa tarde" : "Good afternoon";
                return lang === "pt-PT" ? "Boa noite" : "Good evening";
            },
        };
    }, [lang]);

    return <I18nCtx.Provider value={{ lang, setLang, t, tn, fmt: formatters }}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
    const ctx = useContext(I18nCtx);
    if (!ctx) throw new Error("useI18n must be inside I18nProvider");
    return ctx;
}
